import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchNFTDetail,
  fetchGradingCatalog,
  createNote,
  transferNFT,
  createExposure,
  fetchExposure,
  cancelExposure,
  fetchExposureQr,
  updateVisibility,
  updateNFT,
} from "../api/nft";
import { listCompanies } from "../api/companies";
import FreeTextCombobox from "../components/FreeTextCombobox";
import { AuthContext } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { extractSheldonPrefix } from "../utils/gradeDisplay";
import { resolveApiMessage } from "../utils/apiMessages";
import { maskNumistrailId } from "../utils/mask";
import { applyUsMintCodeToTitle, buildMintBadgeValue } from "../utils/titleFormatting";
import { CountryAuthorityPicker } from "../components/CountryAuthorityPicker";
import { MintPicker } from "../components/MintPicker";
import {
  AVOIRDUPOIS_OUNCE_TO_GRAMS,
  formatSlabTotalWeightInputFromGrams,
  extractSheldonNumeric,
  getWeightInputUnit,
  METAL_SUGGESTION_KEYS,
  parseSlabTotalWeightToGrams,
  sanitizeSlabWeightInput,
  sanitizeYearInput,
  validateCollectionName,
  validateCountry,
  validateDescription,
  validateDenomination,
  validateGradeAttributeIntOptional,
  validateGradeAttributeTextOptional,
  validateGradingSystem,
  validateGradeTermCode,
  normalizeInput,
  normalizeOptionalMetal,
  validateSlabTotalWeight,
  validateMetal,
  validateMint,
  validateSheldonGrade,
  validateYear,
} from "../utils/validation";

const Badge = ({ children, tone = "neutral", ...rest }) => (
  <span className={`nft-detail-badge nft-detail-badge--${tone}`} {...rest}>{children}</span>
);

const LabelBadge = ({ children, tone = "neutral", ...rest }) => (
  <span className={`nft-detail-label-badge nft-detail-label-badge--${tone}`} {...rest}>{children}</span>
);

const ActionButton = ({ tooltip, children, ...buttonProps }) => (
  <span className={`nft-detail-action-tooltip${tooltip ? " nft-detail-action-tooltip--has-message" : ""}`}>
    <button {...buttonProps}>{children}</button>
    {tooltip ? <span className="nft-detail-action-tooltip__bubble" role="tooltip">{tooltip}</span> : null}
  </span>
);

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const FALLBACK_DETAIL_THUMB_UNTRUSTED = `${PUBLIC_URL}/coin-thumb-144.png`;
const FALLBACK_DETAIL_THUMB_TRUSTED = `${PUBLIC_URL}/coin-thumb-144-locked.png`;
const STRIKE_ORDER = ["rd", "rb", "bn", "fb", "fs"];
const SHELDON_DETAILS_CODES = [
  "G_DETAILS",
  "VG_DETAILS",
  "F_DETAILS",
  "VF_DETAILS",
  "XF_DETAILS",
  "AU_DETAILS",
  "UNC_DETAILS",
  "AG_DETAILS",
  "FR_DETAILS",
  "PR_DETAILS",
];

const uniqueDesignationSlugs = (source = []) =>
  Array.from(new Set((source || []).filter(Boolean).map((slug) => String(slug || "").toLowerCase())));

const deriveNumericSheldonInput = (gradeText, numericValue) => {
  const numeric = Number(numericValue);
  if (!Number.isInteger(numeric) || numeric < 1 || numeric > 70) {
    return "";
  }
  const raw = String(gradeText || "").trim();
  const prefix = extractSheldonPrefix(raw) || "MS";
  const useSpace = /^\s*[A-Za-z]{1,4}\s+\d/.test(raw);
  return useSpace ? `${prefix} ${numeric}` : `${prefix}${numeric}`;
};

const buildCoinTitle = (denomination, country, year) => {
  const pieces = [];
  const normalizedDenomination = normalizeInput(denomination);
  const normalizedCountry = normalizeInput(country);
  const normalizedYear = normalizeInput(year ?? "");
  if (normalizedDenomination) pieces.push(normalizedDenomination);
  if (normalizedCountry) pieces.push(normalizedCountry);
  if (normalizedYear) pieces.push(normalizedYear);
  return pieces.join(" ").trim();
};

const fallbackDetailsTerms = () =>
  SHELDON_DETAILS_CODES.map((code) => ({
    code,
    display_name: code.replace("_DETAILS", ""),
  }));

const Section = ({ title, children, right }) => (
  <section className="nft-detail-section">
    <header className="nft-detail-section__header">
      <h3 className="nft-detail-section__title">{title}</h3>
      {right}
    </header>
    <div className="nft-detail-section__body">{children}</div>
  </section>
);

const TimelineItem = ({ entry }) => (
  <div className="nft-detail-timeline__item">
    <div className="nft-detail-timeline__dot" aria-hidden="true" />
    <p className="nft-detail-timeline__date">{entry.displayDate}</p>
    <p className="nft-detail-timeline__text">
      <span className="nft-detail-timeline__type">{entry.type}</span>
      {entry.detail ? ` - ${entry.detail}` : null}
    </p>
  </div>
);

const NOTE_MARKDOWN_RULES = [
  { token: "**", render: (children) => <strong>{children}</strong> },
  { token: "__", render: (children) => <span className="nft-note-underline">{children}</span> },
  { token: "*", render: (children) => <em>{children}</em> },
];

const findMarkdownMatch = (value, startIndex) => {
  let match = null;
  NOTE_MARKDOWN_RULES.forEach((rule) => {
    const index = value.indexOf(rule.token, startIndex);
    if (index === -1) return;
    if (!match || index < match.index || (index === match.index && rule.token.length > match.token.length)) {
      match = { ...rule, index };
    }
  });
  return match;
};

const parseMarkdownInline = (value) => {
  const nodes = [];
  let cursor = 0;
  while (cursor < value.length) {
    const match = findMarkdownMatch(value, cursor);
    if (!match) {
      nodes.push(value.slice(cursor));
      break;
    }
    if (match.index > cursor) {
      nodes.push(value.slice(cursor, match.index));
    }
    const end = value.indexOf(match.token, match.index + match.token.length);
    if (end === -1) {
      nodes.push(match.token);
      cursor = match.index + match.token.length;
      continue;
    }
    const inner = value.slice(match.index + match.token.length, end);
    const innerNodes = parseMarkdownInline(inner);
    nodes.push(match.render(innerNodes));
    cursor = end + match.token.length;
  }
  return nodes;
};

const renderMarkdownLine = (value, keyPrefix) => {
  const nodes = parseMarkdownInline(value);
  return nodes.map((node, index) => {
    if (typeof node === "string") {
      return (
        <React.Fragment key={`${keyPrefix}-text-${index}`}>
          {node}
        </React.Fragment>
      );
    }
    if (React.isValidElement(node)) {
      return React.cloneElement(node, { key: `${keyPrefix}-node-${index}` });
    }
    return null;
  });
};

const renderMarkdown = (value) => {
  const safeValue = (value || "").replace(/\r\n/g, "\n");
  const lines = safeValue.split("\n");
  return lines.map((line, index) => (
    <React.Fragment key={`line-${index}`}>
      {renderMarkdownLine(line, `line-${index}`)}
      {index < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};

const formatKey = (rawKey) => {
  if (!rawKey) return "";
  return rawKey
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeComparableText = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();
const formatValue = (value, emptyValue = "-") => {
  if (value === null || value === undefined) return emptyValue;
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : emptyValue;
  }
  if (typeof value === "object") return JSON.stringify(value);
  const text = String(value).trim();
  return text ? text : emptyValue;
};

const formatSlabWeight = (grams, locale) => {
  if (grams === null || grams === undefined) return null;
  const numeric = Number(grams);
  if (Number.isNaN(numeric)) return null;
  const ounces = numeric / AVOIRDUPOIS_OUNCE_TO_GRAMS;
  const localeCode = locale || undefined;
  const gramsLabel = numeric.toLocaleString(localeCode, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const ouncesLabel = ounces.toLocaleString(localeCode, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return `${gramsLabel} g (${ouncesLabel} oz)`;
};

const renderMetadataValue = (value) => {
  const isUrl = typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"));
  if (isUrl) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#007bff", textDecoration: "underline", wordBreak: "break-all" }}
      >
        {value}
      </a>
    );
  }
  return value;
};

const replaceIdentifier = (text, identifier, label) => {
  if (!text || !identifier || !label) return text;
  return text.split(identifier).join(label);
};

const applyFriendlyNamesToEntry = (entry, maskFn) => {
  if (!entry?.detail) return entry;
  const applyMask = typeof maskFn === "function" ? maskFn : (value) => value;
  let detailText = entry.detail;
  detailText = replaceIdentifier(detailText, entry.to_user_id, applyMask(entry.to_user_numistrail_id));
  detailText = replaceIdentifier(detailText, entry.initiator_user_id, applyMask(entry.initiator_numistrail_id));
  return { ...entry, detail: detailText };
};

const EXPOSURE_POLL_INTERVAL = 4000;
const MAX_PAST_START_MS = 30 * 24 * 60 * 60 * 1000;

const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const minutes = date.getMinutes() - date.getTimezoneOffset();
  date.setMinutes(minutes);
  return date.toISOString().slice(0, 16);
};

const buildDataUrl = (base64) => {
  if (!base64) return null;
  return `data:image/png;base64,${base64}`;
};

const downloadDataUrl = (base64, filename) => {
  if (!base64) return;
  const link = document.createElement("a");
  link.href = buildDataUrl(base64);
  link.download = filename || "numistrail-qr.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


export function NftDetails() {
  const { nftId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const formatDateTime = useCallback((value) => {
    if (!value) return t("common.notAvailable");
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }, [t]);
  const initialSummary = location.state?.nftSummary;
  const [detail, setDetail] = useState(initialSummary || null);
  const [isLoading, setIsLoading] = useState(!initialSummary);
  const [error, setError] = useState(null);
  const [isPrivateAccessError, setIsPrivateAccessError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [consignmentMessage, setConsignmentMessage] = useState(null);
  const [isConsignmentAction, setIsConsignmentAction] = useState(false);
  const [activeExposure, setActiveExposure] = useState(initialSummary?.active_exposure || null);
  const [exposureModal, setExposureModal] = useState({ open: false, step: null, context: null });
  const [exposureError, setExposureError] = useState(null);
  const [exposureLoading, setExposureLoading] = useState(false);
  const [exposureMessage, setExposureMessage] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);
  const [visibilityMessage, setVisibilityMessage] = useState(null);
  const [isVisibilityLoading, setVisibilityLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [privateModalOpen, setPrivateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isEditSubmitting, setEditSubmitting] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  const [editTouched, setEditTouched] = useState({});
  const [editDraft, setEditDraft] = useState({
    country: "",
    mint: "",
    denomination: "",
    year: "",
    slab_total_weight: "",
    gradingSystem: "sheldon",
    gradeMode: "numeric",
    grade: "",
    detailsTerm: "",
    strikeChar: "",
    bonusPlus: false,
    bonusStar: false,
    termCode: "",
    strike: "",
    surface: "",
    style: "",
    detailsFlag: false,
    detailsReason: "",
    collection_name: "",
    description: "",
    promoteTrusted: false,
  });
  const [selectedEditCountryAuthority, setSelectedEditCountryAuthority] = useState(null);
  const [selectedEditMint, setSelectedEditMint] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteMessage, setNoteMessage] = useState(null);
  const [isNoteSubmitting, setIsNoteSubmitting] = useState(false);
  const [isNoteComposerOpen, setNoteComposerOpen] = useState(false);
  const [marketplaces, setMarketplaces] = useState([]);
  const [marketplacesLoading, setMarketplacesLoading] = useState(false);
  const [marketplaceLoadError, setMarketplaceLoadError] = useState(null);
  const [gradingCatalog, setGradingCatalog] = useState({ systems: [], terms: {}, attribute_definitions: {} });
  const [qrPreview, setQrPreview] = useState(null);
  const [qrPayload, setQrPayload] = useState(null);
  const [marketplaceDraft, setMarketplaceDraft] = useState(() => {
    const now = new Date();
    const startAt = toLocalInputValue(now);
    const endAt = toLocalInputValue(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
    return { platform: "", username: "", startAt, endAt };
  });
  const [pollExposureId, setPollExposureId] = useState(null);
  const updateDetailExposure = useCallback((nextExposure, nextState) => {
    setActiveExposure(nextExposure || null);
    setDetail((prev) => {
      if (!prev) return prev;
      const next = { ...prev, active_exposure: nextExposure || null };
      if (nextState) {
        next.state = nextState;
      }
      return next;
    });
  }, []);
  const resetExposureModal = useCallback(() => {
    setExposureError(null);
    setExposureLoading(false);
    setQrPreview(null);
    setQrPayload(null);
  }, []);
  const redirectTimeout = useRef(null);
  const noteInputRef = useRef(null);
  const { userId, company, companyRole, isPlatformAdmin, canCertify, token } = useContext(AuthContext);
  const weightInputLocale = i18n.resolvedLanguage || i18n.language;
  const weightInputUnit = getWeightInputUnit(weightInputLocale);
  const slabWeightUnitLabel = t(
    weightInputUnit === "oz"
      ? "cert.fields.slabWeight.unit.ounces"
      : "cert.fields.slabWeight.unit.grams"
  );
  const slabWeightPlaceholder = t(
    weightInputUnit === "oz"
      ? "cert.fields.slabWeight.placeholder.ounces"
      : "cert.fields.slabWeight.placeholder.grams"
  );
  const metalSuggestions = useMemo(
    () => METAL_SUGGESTION_KEYS.map((key) => t(`cert.fields.metal.suggestions.${key}`)),
    [i18n.resolvedLanguage, t]
  );
  const handleRetry = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);
  const handleCloseError = useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (typeof window !== "undefined" && window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    navigate(token || userId ? "/owner" : "/login", { replace: true });
  }, [navigate, token, userId]);

  const returnTo = typeof location.state?.returnTo === "string" ? location.state.returnTo : "";
  const canShowBack = Boolean(returnTo || token || userId);
  const handleBack = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate(-1);
  }, [navigate, returnTo]);

  const buildEditDraftFromDetail = useCallback((source) => {
    const metadata = source?.metadata || {};
    const coin = source?.coin || {};
    const gradeInfo = source?.grade_info || {};
    const gradingSystem = String(gradeInfo?.system || "sheldon").toLowerCase();
    const termCode = String(gradeInfo?.term_code || "").toUpperCase();
    const designationSlugs = uniqueDesignationSlugs([
      ...(Array.isArray(source?.designation_slugs) ? source.designation_slugs : []),
      ...(Array.isArray(gradeInfo?.designations) ? gradeInfo.designations : []),
    ]);
    const gradeAttributes = Array.isArray(gradeInfo?.attributes) ? gradeInfo.attributes : [];
    const findAttr = (code) =>
      gradeAttributes.find((attr) => String(attr?.code || "").toLowerCase() === code);
    const strikeAttr = findAttr("strike");
    const surfaceAttr = findAttr("surface");
    const styleAttr = findAttr("style");
    const strikeChar = STRIKE_ORDER.find((slug) => designationSlugs.includes(slug)) || "";
    const gradeMode =
      gradingSystem === "sheldon"
        ? termCode === "AUTHENTIC"
          ? "authentic"
          : termCode
            ? "details"
            : "numeric"
        : "numeric";

    return {
      country: metadata.country_raw || coin.country || "",
      mint: metadata.mint_raw || coin.mint || "",
      denomination: coin.denomination || "",
      year: coin.year !== null && coin.year !== undefined ? String(coin.year) : "",
      metal: normalizeOptionalMetal(metadata.metal),
      slab_total_weight: formatSlabTotalWeightInputFromGrams(coin.slab_total_weight_grams, weightInputLocale),
      gradingSystem,
      gradeMode,
      grade:
        gradingSystem === "sheldon" && gradeMode === "numeric"
          ? deriveNumericSheldonInput(source?.grade || "", gradeInfo?.numeric_value)
          : "",
      detailsTerm: gradingSystem === "sheldon" && gradeMode === "details" ? termCode : "",
      strikeChar: gradingSystem === "sheldon" ? strikeChar : "",
      bonusPlus: gradingSystem === "sheldon" ? designationSlugs.includes("plus") : false,
      bonusStar: gradingSystem === "sheldon" ? designationSlugs.includes("star") : false,
      termCode: gradingSystem === "ancient_descriptive" ? termCode : "",
      strike:
        gradingSystem === "ancient_descriptive" && strikeAttr?.value_int !== undefined && strikeAttr?.value_int !== null
          ? String(strikeAttr.value_int)
          : "",
      surface:
        gradingSystem === "ancient_descriptive" && surfaceAttr?.value_int !== undefined && surfaceAttr?.value_int !== null
          ? String(surfaceAttr.value_int)
          : "",
      style: gradingSystem === "ancient_descriptive" ? String(styleAttr?.value_text || "") : "",
      detailsFlag: gradingSystem === "ancient_descriptive" ? Boolean(gradeInfo?.details?.flag) : false,
      detailsReason:
        gradingSystem === "ancient_descriptive" ? String(gradeInfo?.details?.reason || "") : "",
      collection_name: source?.collection_name || metadata.collection_name || metadata.collection || "",
      description: metadata.description || "",
      promoteTrusted: false,
    };
  }, [weightInputLocale]);

  useEffect(() => {
    fetchGradingCatalog()
      .then(({ data }) => {
        const catalog = data && typeof data === "object" ? data : {};
        setGradingCatalog({
          systems: Array.isArray(catalog.systems) ? catalog.systems : [],
          terms: catalog.terms && typeof catalog.terms === "object" ? catalog.terms : {},
          attribute_definitions:
            catalog.attribute_definitions && typeof catalog.attribute_definitions === "object"
              ? catalog.attribute_definitions
              : {},
        });
      })
      .catch(() => {
        setGradingCatalog({ systems: [], terms: {}, attribute_definitions: {} });
      });
  }, []);

  const loadMarketplaces = useCallback(() => {
    setMarketplacesLoading(true);
    setMarketplaceLoadError(null);
    listCompanies({ companyType: "MARKETPLACE" })
      .then(({ data }) => {
        const list = (data?.companies && Array.isArray(data.companies)) ? data.companies : Array.isArray(data) ? data : [];
        setMarketplaces(list);
        setMarketplaceDraft((prev) => {
          if (prev.platform || !list.length) return prev;
          const first = list[0];
          const platformValue = first.slug || first.display_name || "";
          if (!platformValue) return prev;
          return { ...prev, platform: platformValue };
        });
      })
      .catch(() => {
        setMarketplaceLoadError(t("nft.marketplaces.loadError"));
      })
      .finally(() => {
        setMarketplacesLoading(false);
      });
  }, []);

  const matchMarketplace = useCallback(
    (value) => {
      const target = (value || "").toLowerCase();
      if (!target) return null;
      return (
        marketplaces.find((item) => {
          const slug = (item.slug || "").toLowerCase();
          const display = (item.display_name || "").toLowerCase();
          return slug === target || display === target;
        }) || null
      );
    },
    [marketplaces]
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setIsPrivateAccessError(false);
    fetchNFTDetail(nftId)
      .then((response) => {
        if (!isMounted) return;
        setDetail(response.data);
        setActiveExposure(response.data?.active_exposure || null);
        setError(null);
        setIsPrivateAccessError(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        const status = err?.response?.status;
        const key = err?.response?.data?.message_key;
        const rawMessage = String(err?.response?.data?.message || "").toLowerCase();
        const privateAccessDenied =
          key === "errors.nft.not_owner" ||
          (status === 403 && rawMessage.includes("owner"));
        if (privateAccessDenied) {
          setError(t("nft.privateAccess"));
          setIsPrivateAccessError(true);
          return;
        }
        setError(resolveApiMessage(err, t, "nft.loadError", t("nft.loadError")));
        setIsPrivateAccessError(false);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [nftId, reloadKey]);

  useEffect(() => {
    if (!detail) return;
    setEditDraft(buildEditDraftFromDetail(detail));
    setSelectedEditCountryAuthority(
      detail?.coin?.country_authority_code
        ? {
            code: detail.coin.country_authority_code,
            display_name: detail?.metadata?.country_canonical_display || detail.coin.country || "",
          }
        : null
    );
  }, [detail, buildEditDraftFromDetail]);

  const certifyingCompanyId = detail?.digital_certifying_company_id;
  const consignedUserId = detail?.delegated_user_id;
  const consignedUserName = detail?.consigned_user_numistrail_id;
  const ownerId = detail?.owner_id;
  const ownerName = detail?.owner_numistrail_id;
  const certifyingCompanyLabel = detail?.digital_certifying_company_display_name || certifyingCompanyId || t("common.notAvailable");
  const shouldMaskIds = useMemo(() => {
    const isPublicView = Boolean(detail?.is_public);
    if (!isPublicView) return false;
    const isOwnerView = Boolean(detail?.owner_id && userId && detail.owner_id === userId);
    if (isOwnerView || isPlatformAdmin) return false;
    const role = (companyRole || "").toLowerCase();
    const isIntermediaryAdmin = role === "admin" && company?.company_type === "INTERMEDIARY";
    const isConsignedView = Boolean(
      detail?.delegated_user_id &&
      userId &&
      detail.delegated_user_id === userId &&
      isIntermediaryAdmin
    );
    return !isConsignedView;
  }, [detail, userId, companyRole, company, isPlatformAdmin]);
  const maskIdForDisplay = useCallback(
    (value) => (shouldMaskIds ? maskNumistrailId(value) : value),
    [shouldMaskIds]
  );

  const formatMetadataLabel = useCallback((rawKey) => {
    if (!rawKey) return "";
    const keyString = rawKey.toString();
    const normalizedKey = keyString.toLowerCase();
    const translated = t(`nft.meta.${normalizedKey}`, { defaultValue: "" });
    return translated || formatKey(keyString);
  }, [t]);

  const metadataEntries = useMemo(() => {
    if (!detail) return [];
    const metadata = detail.metadata || {};
    const coin = detail.coin || {};
    const collectionName = detail.collection_name || metadata.collection_name || metadata.collection;
    const gradeInfo = detail.grade_info || {};
    const gradingSystem = gradeInfo?.system;
    const gradingSystemLabel = gradingSystem
      ? t(`grade.system.${gradingSystem}`, { defaultValue: gradingSystem })
      : null;
    const gradeDetails = gradeInfo?.details;
    const gradeAttributes = Array.isArray(gradeInfo?.attributes) ? gradeInfo.attributes : [];
    const attributePieces = [];
    const findAttr = (code) => gradeAttributes.find((attr) => String(attr?.code || "").toLowerCase() === code);
    const strike = findAttr("strike");
    if (strike?.value_int !== null && strike?.value_int !== undefined) {
      const max = strike.max_value ?? 5;
      attributePieces.push(`${t("grade.attributes.strike")} ${strike.value_int}/${max}`);
    }
    const surface = findAttr("surface");
    if (surface?.value_int !== null && surface?.value_int !== undefined) {
      const max = surface.max_value ?? 5;
      attributePieces.push(`${t("grade.attributes.surface")} ${surface.value_int}/${max}`);
    }
    const style = findAttr("style");
    if (style?.value_text) {
      attributePieces.push(String(style.value_text));
    }
    const gradeAttributesSummary = attributePieces.filter(Boolean).join(" • ");
    const designationSlugsPrimary = Array.isArray(detail?.designation_slugs) ? detail.designation_slugs : [];
    const designationSlugsFallback = Array.isArray(gradeInfo?.designations) ? gradeInfo.designations : [];
    const designationSlugs = Array.from(
      new Set(
        [...designationSlugsPrimary, ...designationSlugsFallback]
          .filter(Boolean)
          .map((slug) => String(slug || "").toLowerCase())
      )
    );
    const gradeDisplayMeta = (() => {
      const gradeText = detail.grade && String(detail.grade).trim();
      const termCode = (gradeInfo?.term_code || "").toUpperCase();
      const numeric = gradeInfo?.numeric_value;
      const strikeOrder = ["rd", "rb", "bn", "fb", "fs"];
      const parsedPlus = gradeText ? gradeText.includes("+") : false;
      const parsedStar = gradeText ? gradeText.includes("★") || gradeText.includes("\u2605") : false;
      const parsedStrike = gradeText
        ? strikeOrder.find((s) => new RegExp(`\\b${s}\\b`, "i").test(gradeText))
        : undefined;
      const hasPlus = designationSlugs.includes("plus") || parsedPlus;
      const hasStar = designationSlugs.includes("star") || parsedStar;
      const strikeSlug = strikeOrder.find((s) => designationSlugs.includes(s)) || parsedStrike;

      const suffixTokens = [];
      if (hasPlus) suffixTokens.push("+");
      if (strikeSlug) suffixTokens.push(strikeSlug.toUpperCase());
      if (hasStar) suffixTokens.push("★");
      const suffixStr = suffixTokens.length ? suffixTokens.join(" ") : "";

      let baseGrade = "";
      if (termCode === "AUTHENTIC") {
        baseGrade = "Authentic";
      } else if (termCode.endsWith("_DETAILS")) {
        baseGrade = `${termCode.replace("_DETAILS", "")} Details`;
      } else if (typeof numeric === "number") {
        const prefix = extractSheldonPrefix(gradeText) || "MS";
        baseGrade = `${prefix} ${numeric}`;
      } else if (gradeText) {
        const tokens = gradeText.split(/\s+/);
        const filtered = tokens.filter((tok) => {
          const lower = tok.toLowerCase();
          if (lower === "+" || lower === "★" || lower === "\u2605") return false;
          if (strikeOrder.includes(lower)) return false;
          return true;
        });
        baseGrade = filtered.join(" ").trim() || gradeText;
      }

      if (!baseGrade && !suffixStr) return "";
      if (!suffixStr) return baseGrade;
      return `${baseGrade} ${suffixStr}`.trim();
    })();
    
    const rawCountryValue = String(metadata.country_raw || "").trim();
    const canonicalCountryValue = String(metadata.country_canonical_display || coin.country || "").trim();
    const countryDisplayValue = (() => {
      if (rawCountryValue && canonicalCountryValue) {
        if (normalizeComparableText(rawCountryValue) === normalizeComparableText(canonicalCountryValue)) {
          return rawCountryValue;
        }
        return `${rawCountryValue} (${canonicalCountryValue})`;
      }
      return rawCountryValue || canonicalCountryValue;
    })();
    const rawMintValue = String(metadata.mint_raw || "").trim();
    const canonicalMintValue = String(metadata.mint_canonical_display || coin.mint || "").trim();
    const mintDisplayValue = (() => {
      if (rawMintValue && canonicalMintValue) {
        if (normalizeComparableText(rawMintValue) === normalizeComparableText(canonicalMintValue)) {
          return rawMintValue;
        }
        return `${rawMintValue} (${canonicalMintValue})`;
      }
      return rawMintValue || canonicalMintValue;
    })();
    const slabWeight = formatSlabWeight(coin.slab_total_weight_grams, i18n.resolvedLanguage || i18n.language);
    const metadataMetal = normalizeOptionalMetal(metadata.metal);
    const certLookupUrl = (() => {
      const raw = detail.certification_entity_lookup_url;
      if (!raw) return raw;
      const authority = (detail.certification_authority || '').trim().toLowerCase();
      if (authority !== 'ncv') return raw;
      const lang = ((i18n.resolvedLanguage || i18n.language) || "").toLowerCase();
      const isEnglish = lang.startsWith('en');
      try {
        const url = new URL(raw);
        if (url.hostname !== 'buscar.ncvgrading.com') return raw;
        url.pathname = isEnglish ? '/en' : '/';
        return url.toString();
      } catch {
        if (isEnglish) {
          return raw.replace('https://buscar.ncvgrading.com/?ref=', 'https://buscar.ncvgrading.com/en?ref=');
        }
        return raw.replace('https://buscar.ncvgrading.com/en?ref=', 'https://buscar.ncvgrading.com/?ref=');
      }
    })();
    const itemOriginLabel = String(detail.item_origin || "").toUpperCase() === "TRUSTED_CERTIFIED"
      ? t("nft.origin.trusted")
      : t("nft.origin.userGenerated");
    const notProvided = t("common.notProvided");
    const baseEntries = [
      [t("nft.meta.country"), formatValue(countryDisplayValue, notProvided)],
      [t("nft.meta.denomination"), formatValue(coin.denomination, notProvided)],
      [t("nft.meta.year"), formatValue(coin.year, notProvided)],
      [t("nft.meta.mint"), formatValue(mintDisplayValue, notProvided)],
      [t("nft.meta.metal"), formatValue(metadataMetal, notProvided)],
      [t("nft.meta.grade"), gradeDisplayMeta || formatValue(detail.grade, notProvided)],
      ...(gradingSystem ? [[t("nft.meta.gradingSystem"), formatValue(gradingSystemLabel, notProvided)]] : []),
      ...(gradeDetails?.flag ? [[t("nft.meta.gradeDetails"), formatValue(gradeDetails.reason, notProvided)]] : []),
      ...(gradeAttributesSummary ? [[t("nft.meta.gradeAttributes"), gradeAttributesSummary]] : []),
      [t("nft.meta.certNumber"), detail.certification_number],
      [t("nft.meta.certAuthority"), detail.certification_authority],
      [t("nft.meta.itemOrigin"), itemOriginLabel],
      [t("nft.meta.certLookup"), formatValue(certLookupUrl, notProvided)],
      [t("nft.meta.certifiedBy"), certifyingCompanyLabel],
      [t("nft.meta.certifiedAt"), detail.digitally_certified_at ? new Date(detail.digitally_certified_at).toLocaleString() : t("common.notAvailable")],
      ...(collectionName ? [[t("nft.meta.collection"), formatValue(collectionName, notProvided)]] : []),
      [t("nft.meta.description"), formatValue(metadata.description, notProvided)],
      [t("nft.meta.slabWeight"), formatValue(slabWeight, notProvided)],
    ];
    if (consignedUserId) {
      const ownerDisplay = ownerName ? maskIdForDisplay(ownerName) : (ownerId || t("common.notAvailable"));
      const consignedDisplay = consignedUserName ? maskIdForDisplay(consignedUserName) : consignedUserId;
      baseEntries.splice(5, 0, [t("nft.meta.owner"), ownerDisplay]);
      baseEntries.splice(6, 0, [t("nft.meta.consignedTo"), consignedDisplay]);
    }
    const excludedKeys = new Set([
      "schema_version",
      "category",
      "title",
      "country",
      "denomination",
      "year",
      "metal",
      "grade",
      "collection",
      "collection_name",
      "description",
      "country_raw",
      "country_canonical_display",
      "mint_raw",
      "mint_canonical_display",
      "mint_short_code",
    ]);

    return [
      ...baseEntries,
      ...Object.entries(metadata)
        .filter(([key]) => !excludedKeys.has(String(key).toLowerCase()))
        .map(([key, value]) => [formatMetadataLabel(key), formatValue(value, notProvided)]),
    ];
  }, [detail, certifyingCompanyLabel, consignedUserId, consignedUserName, ownerName, ownerId, t, i18n.language, i18n.resolvedLanguage, formatMetadataLabel, maskIdForDisplay]);

  const timelineEntries = useMemo(() => {
    if (!detail?.timeline) return [];
    return detail.timeline.map((entry) => ({
      ...entry,
      type: entry.type_key ? t(entry.type_key, { defaultValue: entry.type }) : entry.type,
      detail: entry.detail_key ? t(entry.detail_key, { ...(entry.detail_params || {}), defaultValue: entry.detail }) : entry.detail,
      displayDate: entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : "",
    }));
  }, [detail, t]);

  const TIMELINE_PREVIEW_COUNT = 5;
  const shouldCollapseTimeline = timelineEntries.length > TIMELINE_PREVIEW_COUNT;
  const [isTimelineExpanded, setTimelineExpanded] = useState(!shouldCollapseTimeline);
  useEffect(() => {
    setTimelineExpanded(!shouldCollapseTimeline);
  }, [shouldCollapseTimeline]);
  useEffect(() => {
    if (!pollExposureId) {
      return undefined;
    }
    let cancelled = false;
    const tick = () => {
      fetchExposure(pollExposureId)
        .then(({ data }) => {
          if (cancelled) return;
          updateDetailExposure(data, data.status === "active" ? "Exposed" : undefined);
          if (!["active", "scheduled"].includes(data.status)) {
            setPollExposureId(null);
          }
        })
        .catch(() => {});
    };
    tick();
    const timer = setInterval(tick, EXPOSURE_POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [pollExposureId, updateDetailExposure]);
  const toggleTimeline = () => setTimelineExpanded((prev) => !prev);
  const timelineToggleLabel = isTimelineExpanded
    ? t("nft.timeline.hide")
    : t("nft.timeline.show", { count: timelineEntries.length });
  const visibleTimelineEntries = isTimelineExpanded || !shouldCollapseTimeline
    ? timelineEntries
    : timelineEntries.slice(-TIMELINE_PREVIEW_COUNT);
  const timelinePreviewLabel = shouldCollapseTimeline && !isTimelineExpanded
    ? t("nft.timeline.preview", { visible: TIMELINE_PREVIEW_COUNT, total: timelineEntries.length })
    : null;
  const timelineHeaderControl = shouldCollapseTimeline ? (
    <button type="button" className="nft-detail-chip" onClick={toggleTimeline}>
      {timelineToggleLabel}
    </button>
  ) : (
    <button type="button" className="nft-detail-chip" disabled>
      {t("common.filter")}
    </button>
  );

  const noteEntries = useMemo(() => {
    const notes = Array.isArray(detail?.notes) ? detail.notes : [];
    return [...notes].sort((a, b) => {
      const timeA = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return timeA - timeB;
    });
  }, [detail]);

  const NOTES_PREVIEW_COUNT = 3;
  const shouldCollapseNotes = noteEntries.length > NOTES_PREVIEW_COUNT;
  const [isNotesExpanded, setNotesExpanded] = useState(!shouldCollapseNotes);
  useEffect(() => {
    setNotesExpanded(!shouldCollapseNotes);
  }, [shouldCollapseNotes]);
  const toggleNotes = () => setNotesExpanded((prev) => !prev);
  const notesToggleLabel = isNotesExpanded
    ? t("nft.notes.hide")
    : t("nft.notes.show", { count: noteEntries.length });
  const visibleNotes = isNotesExpanded || !shouldCollapseNotes
    ? noteEntries
    : noteEntries.slice(-NOTES_PREVIEW_COUNT);
  const notesPreviewLabel = shouldCollapseNotes && !isNotesExpanded
    ? t("nft.notes.preview", { visible: NOTES_PREVIEW_COUNT, total: noteEntries.length })
    : null;

  const normalizedRole = (companyRole || "").toLowerCase();
  const isIntermediaryAdmin = normalizedRole === "admin" && company?.company_type === "INTERMEDIARY";
  const isOwner = Boolean(detail?.owner_id && userId && detail.owner_id === userId);
  const isConsignedAdmin = Boolean(
    detail?.delegated_user_id &&
    userId &&
    detail.delegated_user_id === userId &&
    (isIntermediaryAdmin || isPlatformAdmin)
  );
  const itemOrigin = String(detail?.item_origin || "").toUpperCase();
  const isTrustedOrigin = itemOrigin === "TRUSTED_CERTIFIED";
  const isUserGeneratedOrigin = itemOrigin === "USER_GENERATED";
  const isPublic = Boolean(detail?.is_public);
  const canToggleVisibility = Boolean((isOwner || isPlatformAdmin) && detail?.id);
  const canManageActions = Boolean(isOwner || isConsignedAdmin || isPlatformAdmin);
  const canAddNote = Boolean(isOwner || isConsignedAdmin || isPlatformAdmin);
  const canTrustedPartnerEdit = Boolean(canCertify && isUserGeneratedOrigin);
  const canEditMetadata = Boolean((isOwner && isUserGeneratedOrigin) || canTrustedPartnerEdit);
  const canPromoteToTrusted = canTrustedPartnerEdit;
  const shouldShowActions = Boolean(canManageActions || canEditMetadata);
  const isEditSheldon = normalizeInput(editDraft.gradingSystem, { lowercase: true }) === "sheldon";
  const sheldonDetailsTermOptions = useMemo(() => {
    const terms = gradingCatalog?.terms?.sheldon;
    if (Array.isArray(terms) && terms.length) {
      return terms.filter((term) => {
        const code = String(term?.code || "").toUpperCase();
        return SHELDON_DETAILS_CODES.includes(code);
      });
    }
    return fallbackDetailsTerms();
  }, [gradingCatalog]);
  const ancientTermOptions = useMemo(() => {
    const terms = gradingCatalog?.terms?.ancient_descriptive;
    return Array.isArray(terms) ? terms : [];
  }, [gradingCatalog]);
  const effectiveAncientTermOptions = useMemo(() => {
    if (ancientTermOptions.length) return ancientTermOptions;
    const fallbackCode = normalizeInput(editDraft.termCode, { uppercase: true });
    if (!fallbackCode) return [];
    return [{ code: fallbackCode, display_name: fallbackCode }];
  }, [ancientTermOptions, editDraft.termCode]);
  const strikeOptions = useMemo(() => {
    if (!isEditSheldon || editDraft.gradeMode === "authentic") return [];
    return editDraft.gradeMode === "details" ? ["RD", "RB", "BN"] : ["RD", "RB", "BN", "FB", "FS"];
  }, [editDraft.gradeMode, isEditSheldon]);
  const editValidationErrors = useMemo(() => {
    const normalizedSystem = normalizeInput(editDraft.gradingSystem, { lowercase: true });
    const gradeMode =
      editDraft.gradeMode === "details"
        ? "details"
        : editDraft.gradeMode === "authentic"
          ? "authentic"
          : "numeric";
    const errors = {
      country: validateCountry(editDraft.country),
      mint: validateMint(editDraft.mint),
      denomination: validateDenomination(editDraft.denomination),
      year: validateYear(String(editDraft.year || "")),
      metal: validateMetal(editDraft.metal),
      slab_total_weight: validateSlabTotalWeight(editDraft.slab_total_weight, weightInputLocale),
      gradingSystem: validateGradingSystem(editDraft.gradingSystem),
      grade: null,
      detailsTerm: null,
      strikeChar: null,
      termCode: null,
      strike: null,
      surface: null,
      style: null,
      detailsReason: null,
      collection_name: validateCollectionName(editDraft.collection_name),
      description: validateDescription(editDraft.description),
    };

    if (normalizedSystem === "sheldon") {
      if (gradeMode === "numeric") {
        errors.grade = validateSheldonGrade(editDraft.grade);
      } else if (gradeMode === "details") {
        const detailsTerm = normalizeInput(editDraft.detailsTerm, { uppercase: true });
        errors.detailsTerm = validateGradeTermCode(detailsTerm);
        if (!errors.detailsTerm && !SHELDON_DETAILS_CODES.includes(detailsTerm)) {
          errors.detailsTerm = "validation.grade.term.invalid";
        }
      }

      const strikeSlug = normalizeInput(editDraft.strikeChar, { lowercase: true });
      if (gradeMode === "numeric") {
        if (strikeSlug && !STRIKE_ORDER.includes(strikeSlug)) {
          errors.strikeChar = "validation.grade.term.invalid";
        }
      } else if (gradeMode === "details") {
        if (strikeSlug && !["rd", "rb", "bn"].includes(strikeSlug)) {
          errors.strikeChar = "validation.grade.term.invalid";
        }
      }
    } else if (normalizedSystem === "ancient_descriptive") {
      const termCode = normalizeInput(editDraft.termCode, { uppercase: true });
      errors.termCode = validateGradeTermCode(termCode);
      errors.strike = validateGradeAttributeIntOptional(editDraft.strike);
      errors.surface = validateGradeAttributeIntOptional(editDraft.surface);
      errors.style = validateGradeAttributeTextOptional(editDraft.style);
      if (editDraft.detailsFlag) {
        errors.detailsReason = validateGradeAttributeTextOptional(editDraft.detailsReason);
      }
    }

    return errors;
  }, [
    editDraft.collection_name,
    editDraft.country,
    editDraft.denomination,
    editDraft.description,
    editDraft.detailsFlag,
    editDraft.detailsReason,
    editDraft.detailsTerm,
    editDraft.grade,
    editDraft.gradeMode,
    editDraft.gradingSystem,
    editDraft.mint,
    editDraft.metal,
    editDraft.slab_total_weight,
    editDraft.strikeChar,
    editDraft.strike,
    editDraft.style,
    editDraft.surface,
    editDraft.termCode,
    editDraft.year,
    weightInputLocale,
  ]);
  const isEditValid = useMemo(
    () => Object.values(editValidationErrors).every((value) => !value),
    [editValidationErrors]
  );

  const handleEditCountryInputChange = (nextValue) => {
    setEditDraft((previous) => ({ ...previous, country: nextValue }));
    setEditTouched((previous) => (previous.country ? previous : { ...previous, country: true }));
    setSelectedEditMint(null);
    if (editMessage) {
      setEditMessage(null);
    }
  };

  const handleEditCountrySelect = (authority) => {
    setSelectedEditCountryAuthority(authority || null);
    setSelectedEditMint(null);
  };

  const handleEditCountryClear = () => {
    setSelectedEditCountryAuthority(null);
    setSelectedEditMint(null);
  };

  const handleEditMintInputChange = (nextValue) => {
    setEditDraft((previous) => ({ ...previous, mint: nextValue }));
    setEditTouched((previous) => (previous.mint ? previous : { ...previous, mint: true }));
    if (editMessage) {
      setEditMessage(null);
    }
  };

  const handleEditMintSelect = (mintOption) => {
    setSelectedEditMint(mintOption || null);
  };

  const handleEditMintClear = () => {
    setSelectedEditMint(null);
  };

  const markEditTouched = (field) => () =>
    setEditTouched((previous) => ({ ...previous, [field]: true }));
  const renderEditFieldError = (field) => {
    const errorKey = editTouched[field] ? editValidationErrors[field] : null;
    if (!errorKey) return null;
    return (
      <p className="nft-edit-form__field-error" role="alert">
        {t(errorKey)}
      </p>
    );
  };
  const renderEditFieldHint = (text) => {
    if (!text) return null;
    return <p className="nft-edit-form__field-hint">{text}</p>;
  };

  const isMintingCompanyUser = Boolean(
    company &&
    detail?.digital_certifying_company_id &&
    company.company_id === detail.digital_certifying_company_id &&
    ["admin", "expert"].includes(normalizedRole)
  );
  const noteMaxLength = (isPlatformAdmin || isMintingCompanyUser) ? 2000 : 1000;
  const noteCountLabel = t("nft.notes.count", { count: noteDraft.length, max: noteMaxLength });
  const noteDraftTrimmed = noteDraft.trim();
  const isNoteTooLong = noteDraftTrimmed.length > noteMaxLength;

  const toggleNoteComposer = () => {
    if (!canAddNote) return;
    setNoteMessage(null);
    setNoteComposerOpen((prev) => !prev);
  };

  const handleNoteCancel = () => {
    setNoteDraft("");
    setNoteMessage(null);
    setNoteComposerOpen(false);
  };

  const handleNoteChange = (event) => {
    const value = event.target.value;
    setNoteDraft(value);
    if (noteMessage) {
      setNoteMessage(null);
    }
  };

  const applyNoteFormatting = (token) => () => {
    const input = noteInputRef.current;
    if (!input) return;
    const start = input.selectionStart ?? noteDraft.length;
    const end = input.selectionEnd ?? noteDraft.length;
    const placeholder = t("nft.notes.format.placeholder");
    const hasSelection = start !== end;
    const selectedText = hasSelection ? noteDraft.slice(start, end) : placeholder;
    const nextValue = `${noteDraft.slice(0, start)}${token}${selectedText}${token}${noteDraft.slice(end)}`;
    if (nextValue.length > noteMaxLength) {
      setNoteMessage({ type: "error", text: t("errors.note.too_long", { max: noteMaxLength }) });
      return;
    }
    setNoteDraft(nextValue);
    requestAnimationFrame(() => {
      input.focus();
      const cursorStart = start + token.length;
      const cursorEnd = cursorStart + selectedText.length;
      input.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const handleCreateNote = async (event) => {
    event.preventDefault();
    if (!detail?.id) return;
    if (!canAddNote) {
      setNoteMessage({ type: "error", text: t("errors.note.not_authorized") });
      return;
    }
    const trimmed = noteDraft.trim();
    if (!trimmed) {
      setNoteMessage({ type: "error", text: t("errors.note.empty") });
      return;
    }
    if (trimmed.length > noteMaxLength) {
      setNoteMessage({ type: "error", text: t("errors.note.too_long", { max: noteMaxLength }) });
      return;
    }
    setIsNoteSubmitting(true);
    setNoteMessage(null);
    try {
      const { data } = await createNote(detail.id, { body: trimmed });
      setDetail((prev) => {
        if (!prev) return prev;
        const nextTimeline = data?.transaction
          ? [...(prev.timeline || []), data.transaction]
          : (prev.timeline || []);
        const nextNotes = data?.note
          ? [...(prev.notes || []), data.note]
          : (prev.notes || []);
        return { ...prev, timeline: nextTimeline, notes: nextNotes };
      });
      setNoteDraft("");
      setNoteComposerOpen(false);
      setNoteMessage({ type: "success", text: t("nft.notes.saved") });
    } catch (err) {
      setNoteMessage({
        type: "error",
        text: resolveApiMessage(err, t, "errors.note.invalid", "Unable to add note."),
      });
    } finally {
      setIsNoteSubmitting(false);
    }
  };
  const addNoteDisabled = !canAddNote || isNoteSubmitting;
  const addNoteLabel = isNoteComposerOpen ? t("nft.notes.close") : t("nft.actions.addNote");
  const notesHeaderControl = (
    <div className="nft-detail-section__controls">
      {shouldCollapseNotes && (
        <button type="button" className="nft-detail-chip" onClick={toggleNotes}>
          {notesToggleLabel}
        </button>
      )}
      <button
        type="button"
        className="nft-detail-chip"
        onClick={toggleNoteComposer}
        disabled={addNoteDisabled}
        title={!canAddNote ? t("nft.notes.addDisabled") : undefined}
      >
        {addNoteLabel}
      </button>
    </div>
  );

  const exposureStatus = activeExposure?.status;
  const hasActiveExposure = Boolean(activeExposure && ["active", "scheduled"].includes(exposureStatus));
  const verificationLabel = hasActiveExposure
    ? activeExposure.exposure_type === "present"
      ? t("nft.verification.inPerson")
      : t("nft.verification.marketplace")
    : t("nft.verification.none");
  const verificationTone = hasActiveExposure ? "positive" : "neutral";
  const isRemoteExposure = activeExposure?.exposure_type === "not_present" && hasActiveExposure;
  const isFaceToFaceExposure = activeExposure?.exposure_type === "present";
  const metadata = detail?.metadata || {};
  const coin = detail?.coin || {};
  const structuredTitle = [coin.denomination, coin.country, coin.year].filter(Boolean).join(" ").trim();
  const rawTitle = metadata.title || structuredTitle || metadata.description || detail?.title || `Item #${detail?.id ?? ""}`;
  const title = applyUsMintCodeToTitle(rawTitle, coin, metadata);
  const collectionName = detail?.collection_name || metadata.collection_name || metadata.collection;
  const grade = detail?.grade || metadata.grade;
  const gradeInfo = detail?.grade_info;
  const designationSlugsPrimary = Array.isArray(detail?.designation_slugs) ? detail.designation_slugs : [];
  const designationSlugsFallback = Array.isArray(gradeInfo?.designations) ? gradeInfo.designations : [];
  const designationSlugs = Array.from(
    new Set(
      [...designationSlugsPrimary, ...designationSlugsFallback].filter(Boolean).map((slug) => String(slug || "").toLowerCase())
    )
  );
  const gradeAttributes = Array.isArray(gradeInfo?.attributes) ? gradeInfo.attributes : [];
  const gradeAttributePieces = [];
  const findGradeAttr = (code) => gradeAttributes.find((attr) => String(attr?.code || "").toLowerCase() === code);
  const strike = findGradeAttr("strike");
  if (strike?.value_int !== null && strike?.value_int !== undefined) {
    const max = strike.max_value ?? 5;
    gradeAttributePieces.push(`${t("grade.attributes.strike")} ${strike.value_int}/${max}`);
  }
  const surface = findGradeAttr("surface");
  if (surface?.value_int !== null && surface?.value_int !== undefined) {
    const max = surface.max_value ?? 5;
    gradeAttributePieces.push(`${t("grade.attributes.surface")} ${surface.value_int}/${max}`);
  }
  const style = findGradeAttr("style");
  if (style?.value_text) {
    gradeAttributePieces.push(String(style.value_text));
  }
  const gradeAttributesSummary = gradeAttributePieces.filter(Boolean).join(" • ");
  const gradeDisplay = (() => {
    const info = gradeInfo || {};
    const termCode = (info?.term_code || "").toUpperCase();
    const numeric = info?.numeric_value;
    const strikeOrder = ["rd", "rb", "bn", "fb", "fs"];
    const gradeText = grade && String(grade).trim();
    const parsedPlus = gradeText ? gradeText.includes("+") : false;
    const parsedStar = gradeText ? gradeText.includes("★") || gradeText.includes("\u2605") : false;
    const parsedStrike = gradeText
      ? strikeOrder.find((s) => new RegExp(`\\b${s}\\b`, "i").test(gradeText))
      : undefined;
    const hasPlus = designationSlugs.includes("plus") || parsedPlus;
    const hasStar = designationSlugs.includes("star") || parsedStar;
    const strikeSlug = strikeOrder.find((s) => designationSlugs.includes(s)) || parsedStrike;

    const suffixTokens = [];
    if (hasPlus) suffixTokens.push("+");
    if (strikeSlug) suffixTokens.push(strikeSlug.toUpperCase());
    if (hasStar) suffixTokens.push("\u2605");
    const suffixStr = suffixTokens.length ? suffixTokens.join(" ") : "";

    let baseGrade = "";
    if (termCode === "AUTHENTIC") {
      baseGrade = "Authentic";
    } else if (termCode.endsWith("_DETAILS")) {
      baseGrade = `${termCode.replace("_DETAILS", "")} Details`;
    } else if (typeof numeric === "number") {
      const prefix = extractSheldonPrefix(gradeText) || "MS";
      baseGrade = `${prefix} ${numeric}`;
    } else if (gradeText) {
      const tokens = gradeText.split(/\s+/);
      const filtered = tokens.filter((tok) => {
        const lower = tok.toLowerCase();
        if (lower === "+" || lower === "\u2605" || lower === "★") return false;
        if (strikeOrder.includes(lower)) return false;
        return true;
      });
      baseGrade = filtered.join(" ").trim() || gradeText;
    }

    if (!baseGrade && !suffixStr) return "";
    if (!suffixStr) return baseGrade;
    return `${baseGrade} ${suffixStr}`.trim();
  })();
  const year = coin.year ?? metadata.year;
  const mintBadgeValue = buildMintBadgeValue(coin, metadata);
  const metal = normalizeOptionalMetal(metadata.metal);
  const heroThumbSrc = detail?.heroImageUrl
    || detail?.hero_image_url
    || detail?.thumbnailUrl
    || detail?.thumbnail_url
    || (isTrustedOrigin ? FALLBACK_DETAIL_THUMB_TRUSTED : FALLBACK_DETAIL_THUMB_UNTRUSTED);
  const originLabel = isTrustedOrigin ? t("nft.origin.trusted") : t("nft.origin.userGenerated");
  const originTooltip = isTrustedOrigin ? t("nft.origin.tooltip.trusted") : t("nft.origin.tooltip.userGenerated");
  const visibilityBadgeLabel = isPublic ? t("nft.visibility.badgePublic") : t("nft.visibility.badgePrivate");
  const visibilityButtonLabel = isPublic ? t("nft.visibility.makePrivate") : t("nft.visibility.makePublic");
  const shareForVerificationDisabledReason = !isTrustedOrigin
    ? t("nft.actionsHelpDisabled.shareForVerificationTrustedOnly")
    : consignedUserId
      ? t("nft.actionsHelpDisabled.shareForVerificationConsigned")
      : null;
  const isShareForVerificationDisabled = Boolean(shareForVerificationDisabledReason);
  const shareForVerificationTooltip = shareForVerificationDisabledReason || t("nft.actionsHelp.shareForVerification");
  const shareLinkTooltip = isPublic ? t("nft.actionsHelp.shareLinkPublic") : t("nft.actionsHelp.shareLinkPrivate");
  const visibilityTooltip = isPublic ? t("nft.actionsHelp.makePrivate") : t("nft.actionsHelp.makePublic");
  const transferDisabledReason = consignedUserId && isOwner && !isPlatformAdmin
    ? t("nft.actionsHelpDisabled.transferConsignedOwner")
    : null;
  const isTransferDisabled = Boolean(transferDisabledReason);
  const transferTooltip = transferDisabledReason || t("nft.actionsHelp.transfer");

  const handleTransfer = () => {
    if (!detail) return;
    navigate("/reassign", {
      state: {
        transferDefaults: {
          cert: detail.certification_number,
          auth: detail.certification_authority,
        },
      },
    });
  };

  const handleOpenEdit = () => {
    if (!detail || !canEditMetadata) return;
    setEditMessage(null);
    setEditTouched({});
    setEditDraft(buildEditDraftFromDetail(detail));
    setSelectedEditCountryAuthority(
      detail?.coin?.country_authority_code
        ? {
            code: detail.coin.country_authority_code,
            display_name: detail?.metadata?.country_canonical_display || detail.coin.country || "",
          }
        : null
    );
    setSelectedEditMint(
      detail?.coin?.mint_code
        ? {
            code: detail.coin.mint_code,
            short_code: detail?.coin?.mint_short_code || detail?.metadata?.mint_short_code || "",
            display_name: detail?.metadata?.mint_canonical_display || detail?.coin?.mint || "",
          }
        : null
    );
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    if (isEditSubmitting) return;
    setEditModalOpen(false);
    setEditMessage(null);
    setEditTouched({});
    setSelectedEditMint(null);
  };

  const handleEditFieldChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    const nextValue = field === "slab_total_weight"
      ? sanitizeSlabWeightInput(value, weightInputLocale)
      : field === "year"
        ? sanitizeYearInput(value)
      : value;
    setEditDraft((previous) => {
      const next = { ...previous, [field]: nextValue };
      if (field === "gradingSystem") {
        const system = normalizeInput(value, { lowercase: true });
        if (system === "ancient_descriptive") {
          if (!next.termCode && ancientTermOptions.length) {
            next.termCode = ancientTermOptions[0]?.code || "";
          }
          next.grade = "";
          next.gradeMode = "numeric";
          next.detailsTerm = "";
          next.strikeChar = "";
          next.bonusPlus = false;
          next.bonusStar = false;
        } else {
          next.termCode = "";
          next.strike = "";
          next.surface = "";
          next.style = "";
          next.detailsFlag = false;
          next.detailsReason = "";
          next.gradeMode = "numeric";
          next.detailsTerm = "";
          next.strikeChar = "";
          next.bonusPlus = false;
          next.bonusStar = false;
        }
      } else if (field === "gradeMode") {
        const mode = String(value || "");
        if (mode === "details") {
          next.grade = "";
          next.bonusPlus = false;
          next.bonusStar = false;
          next.strikeChar = "";
        } else if (mode === "authentic") {
          next.grade = "";
          next.detailsTerm = "";
          next.bonusPlus = false;
          next.bonusStar = false;
          next.strikeChar = "";
        } else {
          next.detailsTerm = "";
        }
      } else if (field === "detailsFlag" && !value) {
        next.detailsReason = "";
      }
      return next;
    });
    if (field === "country") {
      setSelectedEditCountryAuthority(null);
      setSelectedEditMint(null);
    }
    if (field === "year" || field === "mint") {
      setSelectedEditMint(null);
    }
    setEditTouched((previous) => (previous[field] ? previous : { ...previous, [field]: true }));
    if (editMessage) {
      setEditMessage(null);
    }
  };

  const handleSubmitEdit = async (event) => {
    event.preventDefault();
    if (!detail?.id) return;

    const fieldsToTouch = [
      "country",
      "mint",
      "denomination",
      "year",
      "metal",
      "slab_total_weight",
      "gradingSystem",
      "grade",
      "detailsTerm",
      "strikeChar",
      "termCode",
      "strike",
      "surface",
      "style",
      "detailsReason",
      "collection_name",
      "description",
    ];
    if (!isEditValid) {
      setEditTouched((previous) => {
        const next = { ...previous };
        fieldsToTouch.forEach((field) => {
          next[field] = true;
        });
        return next;
      });
      const firstError = fieldsToTouch.map((field) => editValidationErrors[field]).find(Boolean);
      setEditMessage({
        type: "error",
        text: t(firstError || "errors.nft.update_failed"),
      });
      return;
    }

    const country = normalizeInput(editDraft.country);
    const mint = normalizeInput(editDraft.mint);
    const denomination = normalizeInput(editDraft.denomination);
    const yearRaw = normalizeInput(editDraft.year);
    const year = Number(yearRaw);
    const metal = normalizeOptionalMetal(editDraft.metal);
    const slabWeightGrams = parseSlabTotalWeightToGrams(editDraft.slab_total_weight, weightInputLocale);

    const normalizedSystem = normalizeInput(editDraft.gradingSystem || "sheldon", { lowercase: true }) || "sheldon";
    const gradeMode =
      editDraft.gradeMode === "details"
        ? "details"
        : editDraft.gradeMode === "authentic"
          ? "authentic"
          : "numeric";
    const gradeInfo = { system: normalizedSystem };
    const attributes = [];
    const designations = [];
    let gradeValue;

    if (normalizedSystem === "sheldon") {
      if (gradeMode === "numeric") {
        const normalizedGrade = normalizeInput(editDraft.grade);
        const gradeError = validateSheldonGrade(normalizedGrade);
        if (gradeError) {
          setEditMessage({ type: "error", text: t(gradeError) });
          return;
        }
        const numericValue = extractSheldonNumeric(normalizedGrade);
        if (numericValue === null) {
          setEditMessage({ type: "error", text: t("validation.grade.sheldon.range") });
          return;
        }
        gradeInfo.numeric_value = numericValue;
        gradeValue = normalizedGrade;
      } else if (gradeMode === "details") {
        const detailsTerm = normalizeInput(editDraft.detailsTerm, { uppercase: true });
        const termError = validateGradeTermCode(detailsTerm);
        if (termError) {
          setEditMessage({ type: "error", text: t(termError) });
          return;
        }
        if (!SHELDON_DETAILS_CODES.includes(detailsTerm)) {
          setEditMessage({ type: "error", text: t("validation.grade.term.invalid") });
          return;
        }
        gradeInfo.term_code = detailsTerm;
        gradeInfo.details = { flag: true };
      } else {
        gradeInfo.term_code = "AUTHENTIC";
      }

      const strikeSlug = normalizeInput(editDraft.strikeChar, { lowercase: true });
      if (gradeMode === "numeric") {
        if (strikeSlug && !STRIKE_ORDER.includes(strikeSlug)) {
          setEditMessage({ type: "error", text: t("validation.grade.term.invalid") });
          return;
        }
        if (strikeSlug) {
          designations.push(strikeSlug);
        }
        if (editDraft.bonusPlus) {
          designations.push("plus");
        }
        if (editDraft.bonusStar) {
          designations.push("star");
        }
      } else if (gradeMode === "details") {
        if (["rd", "rb", "bn"].includes(strikeSlug)) {
          designations.push(strikeSlug);
        } else if (strikeSlug) {
          setEditMessage({ type: "error", text: t("validation.grade.term.invalid") });
          return;
        }
      }
      gradeInfo.designations = designations;
    } else if (normalizedSystem === "ancient_descriptive") {
      const termCode = normalizeInput(editDraft.termCode, { uppercase: true });
      const termError = validateGradeTermCode(termCode);
      if (termError) {
        setEditMessage({ type: "error", text: t(termError) });
        return;
      }
      gradeInfo.term_code = termCode;

      const strikeRaw = normalizeInput(editDraft.strike);
      const strikeError = validateGradeAttributeIntOptional(strikeRaw);
      if (strikeError) {
        setEditMessage({ type: "error", text: t(strikeError) });
        return;
      }
      if (strikeRaw) {
        attributes.push({ code: "strike", value_int: Number(strikeRaw) });
      }

      const surfaceRaw = normalizeInput(editDraft.surface);
      const surfaceError = validateGradeAttributeIntOptional(surfaceRaw);
      if (surfaceError) {
        setEditMessage({ type: "error", text: t(surfaceError) });
        return;
      }
      if (surfaceRaw) {
        attributes.push({ code: "surface", value_int: Number(surfaceRaw) });
      }

      const styleValue = normalizeInput(editDraft.style);
      const styleError = validateGradeAttributeTextOptional(styleValue);
      if (styleError) {
        setEditMessage({ type: "error", text: t(styleError) });
        return;
      }
      if (styleValue) {
        attributes.push({ code: "style", value_text: styleValue });
      }
      gradeInfo.attributes = attributes;

      const detailsReason = normalizeInput(editDraft.detailsReason);
      const detailsReasonError = editDraft.detailsFlag
        ? validateGradeAttributeTextOptional(detailsReason)
        : null;
      if (detailsReasonError) {
        setEditMessage({ type: "error", text: t(detailsReasonError) });
        return;
      }
      gradeInfo.details = {
        flag: Boolean(editDraft.detailsFlag),
        ...(detailsReason ? { reason: detailsReason } : {}),
      };
    } else {
      setEditMessage({ type: "error", text: t("validation.grade.system.invalid") });
      return;
    }

    const payload = {
      country,
      ...(selectedEditCountryAuthority?.code ? { country_ref_code: selectedEditCountryAuthority.code } : {}),
      mint: mint || null,
      ...(selectedEditMint?.code ? { mint_ref_code: selectedEditMint.code } : {}),
      denomination,
      year,
      ...(gradeValue ? { grade: gradeValue } : {}),
      grade_info: gradeInfo,
      metal: metal || null,
      slab_total_weight_grams: slabWeightGrams,
      title: buildCoinTitle(denomination, selectedEditCountryAuthority?.display_name || country, year),
      description: normalizeInput(editDraft.description),
      collection_name: normalizeInput(editDraft.collection_name),
    };
    if (canPromoteToTrusted && editDraft.promoteTrusted) {
      payload.item_origin = "TRUSTED_CERTIFIED";
    }

    setEditSubmitting(true);
    setEditMessage(null);
    try {
      await updateNFT(detail.id, payload);
      setEditMessage({ type: "success", text: t("nft.edit.saved") });
      setEditModalOpen(false);
      setReloadKey((current) => current + 1);
    } catch (err) {
      setEditMessage({
        type: "error",
        text: resolveApiMessage(err, t, "errors.nft.update_failed", "Unable to update item."),
      });
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleConsignmentReturn = async () => {
    if (!detail) return;
    setIsConsignmentAction(true);
    setConsignmentMessage(null);
    try {
      await transferNFT(
        detail.certification_number,
        detail.certification_authority,
        undefined,
        { consignmentReturn: true, nftId: detail.id }
      );
      setDetail((prev) => (prev ? { ...prev, delegated_user_id: null } : prev));
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
      setConsignmentMessage({ type: "success", text: t("nft.consignment.returned") });
      redirectTimeout.current = setTimeout(() => {
        navigate("/owner");
      }, 1500);
    } catch (err) {
      setConsignmentMessage({
        type: "error",
        text: resolveApiMessage(err, t, "errors.generic", "Failed to return consignment."),
      });
    } finally {
      setIsConsignmentAction(false);
    }
  };

  const performVisibilityUpdate = async (nextPublic, successKey) => {
    if (!detail?.id || !canToggleVisibility) return null;
    setVisibilityLoading(true);
    setVisibilityMessage(null);
    try {
      const { data } = await updateVisibility(detail.id, nextPublic);
      setDetail((prev) => (prev ? { ...prev, is_public: data.is_public } : prev));
      const key = successKey || (data.is_public ? "nft.visibility.nowPublic" : "nft.visibility.nowPrivateLinks");
      setVisibilityMessage({ type: "success", text: t(key) });
      return data.is_public;
    } catch (err) {
      setVisibilityMessage({
        type: "error",
        text: resolveApiMessage(err, t, "errors.nft.not_owner", "Unable to update visibility."),
      });
      return null;
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!detail?.id || !canToggleVisibility) return;
    if (isPublic) {
      setPrivateModalOpen(true);
      return;
    }
    await performVisibilityUpdate(true);
  };

  const handleShareLink = async () => {
    if (!detail?.id) return;
    setShareMessage(null);
    const shareUrl = `${window.location.origin}/nft/${detail.id}`;
    const shareText = t("nft.share.text", { url: shareUrl });
    const isWindows = typeof navigator !== "undefined" && /Windows/i.test(navigator.userAgent || "");
    const shareData = isWindows
      ? { title: detail.title || title, text: shareText, url: shareUrl }
      : { title: detail.title || title, text: shareText };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareMessage({ type: "info", text: t("nft.share.sharedDevice") });
        return;
      }
    } catch (err) {
      // ignore AbortError; fall through to clipboard
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        setShareMessage({ type: "info", text: t("nft.share.copied") });
        return;
      }
    } catch (err) {
      // fall back to prompt
    }

    const fallback = window.prompt(t("nft.share.prompt"), shareText);
    if (fallback !== null) {
      setShareMessage({ type: "info", text: t("nft.share.ready") });
    }
  };
  const handleShareClick = () => {
    if (!detail?.id) return;
    if (isPublic) {
      handleShareLink();
      return;
    }
    setShareModalOpen(true);
  };

  const handleExposeClick = () => {
    if (!detail) return;
    if (!isTrustedOrigin) {
      setExposureMessage({ type: "error", text: t("nft.exposure.trustedOnly") });
      return;
    }
    if (detail.delegated_user_id) {
      setExposureMessage({ type: "error", text: t("nft.exposure.consignedError") });
      return;
    }
    resetExposureModal();
    const now = new Date();
    setMarketplaceDraft((prev) => ({
      ...prev,
      startAt: toLocalInputValue(now),
      endAt: toLocalInputValue(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)),
    }));
    if (!marketplaces.length && !marketplacesLoading) {
      loadMarketplaces();
    }
    setExposureModal({ open: true, step: "mode", context: null });
  };

  const startFaceToFaceExposure = async () => {
    if (!detail) return;
    resetExposureModal();
    setExposureModal({ open: true, step: "present", context: null });
    setExposureLoading(true);
    try {
      const { data } = await createExposure(detail.id, { mode: "present" });
      updateDetailExposure(data.exposure, "Exposed");
      setQrPreview(data.qr_png);
      setQrPayload(data.qr_payload);
      setPollExposureId(data.exposure.id);
    } catch (err) {
      setExposureError(resolveApiMessage(err, t, "errors.exposure.invalid", "Unable to start Face-to-Face exposure."));
    } finally {
      setExposureLoading(false);
    }
  };

  const handleExposureModeSelect = (mode) => {
    if (mode === "present") {
      startFaceToFaceExposure();
    } else {
      resetExposureModal();
      if (!marketplaces.length && !marketplacesLoading) {
        loadMarketplaces();
      }
      setExposureModal({ open: true, step: "marketplace", context: null });
    }
  };

  const handleMarketplaceFieldChange = (field) => (event) => {
    const value = event.target.value;
    setMarketplaceDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateMarketplaceExposure = async (event) => {
    event.preventDefault();
    if (!detail) return;
    setExposureLoading(true);
    setExposureError(null);
    const platformValue = (marketplaceDraft.platform || "").trim();
    const username = (marketplaceDraft.username || "").trim();
    const startDate = marketplaceDraft.startAt ? new Date(marketplaceDraft.startAt) : null;
    const endDate = marketplaceDraft.endAt ? new Date(marketplaceDraft.endAt) : null;
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - MAX_PAST_START_MS);
    if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setExposureError(t("errors.exposure.start_end_required"));
      setExposureLoading(false);
      return;
    }
    if (startDate < thirtyDaysAgo) {
      setExposureError(t("errors.exposure.start_too_old"));
      setExposureLoading(false);
      return;
    }
    if (startDate >= endDate) {
      setExposureError(t("errors.exposure.end_before_start"));
      setExposureLoading(false);
      return;
    }
    if (!platformValue) {
      setExposureError(t("nft.exposure.marketplaceRequired"));
      setExposureLoading(false);
      return;
    }
    const selectedMarketplace = matchMarketplace(platformValue);
    if (!selectedMarketplace) {
      setExposureError(t("nft.exposure.marketplaceUnsupported"));
      setExposureLoading(false);
      return;
    }
    if (!username || !/^[A-Za-z0-9._@-]{2,64}$/.test(username)) {
      setExposureError(t("errors.exposure.username_invalid"));
      setExposureLoading(false);
      return;
    }
    try {
      const { data } = await createExposure(detail.id, {
        mode: "not_present",
        platform_name: selectedMarketplace.display_name || selectedMarketplace.slug || platformValue,
        platform_username: username,
        start_at: startDate.toISOString(),
        end_at: endDate.toISOString(),
      });
      updateDetailExposure(data.exposure, "Exposed");
      setExposureModal({ open: false, step: null, context: null });
      setExposureMessage({ type: "success", text: t("nft.exposure.listingShared", { platform: data.exposure.platform_name }) });
    } catch (err) {
      setExposureError(resolveApiMessage(err, t, "errors.exposure.invalid", "Unable to expose item."));
    } finally {
      setExposureLoading(false);
    }
  };

  const handleStopExposure = async () => {
    if (!activeExposure) return;
    try {
      await cancelExposure(activeExposure.id);
      const fallbackState = detail?.delegated_user_id ? "Consigned" : "Private";
      updateDetailExposure(null, fallbackState);
      setExposureMessage({ type: "success", text: t("nft.exposure.stopped") });
    } catch (err) {
      setExposureMessage({
        type: "error",
        text: resolveApiMessage(err, t, "errors.exposure.invalid", "Failed to stop exposure."),
      });
    }
  };

  const handleShowQr = async () => {
    if (!activeExposure) return;
    resetExposureModal();
    setExposureModal({ open: true, step: "remote-qr", context: { platform: activeExposure.platform_name } });
    setExposureLoading(true);
    try {
      const { data } = await fetchExposureQr(activeExposure.id);
      setQrPreview(data.qr_png);
      setQrPayload(data.qr_payload);
      updateDetailExposure(data.exposure);
    } catch (err) {
      setExposureError(resolveApiMessage(err, t, "errors.exposure.qr_unavailable", "Unable to load QR code."));
    } finally {
      setExposureLoading(false);
    }
  };

  const handleCloseExposureModal = useCallback(async (options = {}) => {
    const { skipCancel = false } = options;
    const shouldCancel = !skipCancel && exposureModal.step === "present";
    setExposureModal({ open: false, step: null, context: null });
    resetExposureModal();
    setPollExposureId(null);
    if (shouldCancel && activeExposure?.exposure_type === "present") {
      const isActive = ["active", "scheduled"].includes(activeExposure.status);
      if (isActive) {
        try {
          await cancelExposure(activeExposure.id);
        } catch (err) {
          setExposureMessage({
            type: "error",
            text: resolveApiMessage(err, t, "errors.exposure.invalid", "Unable to cancel exposure."),
          });
        }
      }
      const fallbackState = detail?.delegated_user_id ? "Consigned" : "Private";
      updateDetailExposure(null, fallbackState);
    }
  }, [activeExposure, detail, exposureModal.step, resetExposureModal, updateDetailExposure]);

  const confirmShareMakePublic = async () => {
    const result = await performVisibilityUpdate(true, "nft.visibility.nowPublic");
    if (result === true) {
      setShareModalOpen(false);
      await handleShareLink();
    }
  };

  const confirmMakePrivate = async () => {
    const result = await performVisibilityUpdate(false, "nft.visibility.nowPrivateLinks");
    if (result === false) {
      setPrivateModalOpen(false);
    }
  };

  const renderConfirmModal = ({ open, title, body, confirmLabel, cancelLabel, onConfirm, onCancel }) => {
    if (!open) return null;
    return (
      <div className="exposure-modal" role="dialog" aria-modal="true">
        <div className="exposure-modal__backdrop" onClick={onCancel} />
        <div className="exposure-modal__card">
          <header className="exposure-modal__header">
            <h3>{title}</h3>
            <button type="button" className="exposure-modal__close" onClick={onCancel}>
              ×
            </button>
          </header>
          <div className="exposure-modal__body">
            <p>{body}</p>
          </div>
          <div className="exposure-modal__actions">
            <button
              type="button"
              className="exposure-modal__button exposure-modal__button--secondary"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="exposure-modal__button exposure-modal__button--primary"
              onClick={onConfirm}
              disabled={isVisibilityLoading}
            >
              {isVisibilityLoading ? t("common.saving") : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editModalOpen || !detail) return null;
    return (
      <div className="exposure-modal" role="dialog" aria-modal="true">
        <div className="exposure-modal__backdrop" onClick={handleCloseEdit} />
        <div className="exposure-modal__card">
          <header className="exposure-modal__header">
            <h2>{t("nft.edit.title")}</h2>
            <button type="button" className="exposure-modal__close" onClick={handleCloseEdit}>
              ×
            </button>
          </header>
          <form className="nft-edit-form" onSubmit={handleSubmitEdit}>
            <p className="nft-edit-form__hint">
              {t("nft.edit.lockedIdentifiers", {
                authority: detail.certification_authority,
                number: detail.certification_number,
              })}
            </p>
            <label>
              {t("nft.meta.country")}
              <CountryAuthorityPicker
                id="country"
                value={editDraft.country}
                selectedAuthority={selectedEditCountryAuthority}
                gradingSystem={editDraft.gradingSystem}
                onChangeValue={handleEditCountryInputChange}
                onAuthoritySelect={handleEditCountrySelect}
                onClearAuthority={handleEditCountryClear}
                onBlur={markEditTouched("country")}
                className="cert-input"
                placeholder={t("cert.fields.country.placeholder")}
              />
              {renderEditFieldError("country")}
            </label>
            <label>
              {t("nft.meta.denomination")}
              <input
                type="text"
                value={editDraft.denomination}
                onChange={handleEditFieldChange("denomination")}
                onBlur={markEditTouched("denomination")}
                className="cert-input"
                required
              />
              {renderEditFieldError("denomination")}
            </label>
            <label>
              {t("nft.meta.year")}
              <input
                type="number"
                value={editDraft.year}
                onChange={handleEditFieldChange("year")}
                onBlur={markEditTouched("year")}
                className="cert-input"
                inputMode="numeric"
                required
              />
              {renderEditFieldError("year")}
            </label>
            <label>
              {t("cert.fields.mint.label")}
              <MintPicker
                id="mint"
                value={editDraft.mint}
                selectedMint={selectedEditMint}
                gradingSystem={editDraft.gradingSystem}
                countryRefCode={selectedEditCountryAuthority?.code || detail?.coin?.country_authority_code}
                year={editDraft.year}
                onChangeValue={handleEditMintInputChange}
                onMintSelect={handleEditMintSelect}
                onClearMint={handleEditMintClear}
                onBlur={markEditTouched("mint")}
                className="cert-input"
                placeholder={t("cert.fields.mint.placeholder")}
              />
              {renderEditFieldError("mint")}
            </label>
            <label>
              {t("nft.meta.metal")}
              <FreeTextCombobox
                value={editDraft.metal}
                onChange={handleEditFieldChange("metal")}
                onBlur={markEditTouched("metal")}
                className="cert-input"
                placeholder={t("cert.fields.metal.placeholder")}
                options={metalSuggestions}
              />
              {renderEditFieldHint(t("cert.fields.metal.hint"))}
              {renderEditFieldError("metal")}
            </label>
            <label>
              {t("cert.fields.slabWeight.label")}
              <input
                type="text"
                value={editDraft.slab_total_weight}
                onChange={handleEditFieldChange("slab_total_weight")}
                onBlur={markEditTouched("slab_total_weight")}
                className="cert-input"
                placeholder={slabWeightPlaceholder}
                inputMode="decimal"
              />
              {renderEditFieldHint(t("cert.fields.slabWeight.hint", { unit: slabWeightUnitLabel }))}
              {renderEditFieldError("slab_total_weight")}
            </label>
            <label>
              {t("cert.fields.gradingSystem.label")}
              <select
                className="cert-input cert-input--select"
                value={editDraft.gradingSystem}
                onChange={handleEditFieldChange("gradingSystem")}
                onBlur={markEditTouched("gradingSystem")}
              >
                {(gradingCatalog.systems.length
                  ? gradingCatalog.systems
                  : [{ code: "sheldon" }, { code: "ancient_descriptive" }]
                ).map((system) => (
                  <option key={system.code} value={system.code}>
                    {t(`cert.fields.gradingSystem.options.${system.code}`, {
                      defaultValue: system.name || system.code,
                    })}
                  </option>
                ))}
              </select>
              {renderEditFieldHint(t("cert.fields.gradingSystem.hint", { defaultValue: "Select the grading system used on the slab." }))}
              {renderEditFieldError("gradingSystem")}
            </label>
            {isEditSheldon ? (
              <>
                <div className="cert-radio-inline">
                  <label className="cert-radio-inline__item">
                    <input
                      type="radio"
                      name="edit-grade-mode"
                      value="numeric"
                      checked={editDraft.gradeMode === "numeric"}
                      onChange={handleEditFieldChange("gradeMode")}
                    />
                    <span className="cert-radio-inline__text">
                      {t("cert.fields.gradeMode.numeric", { defaultValue: "Numeric (1-70)" })}
                    </span>
                  </label>
                  <label className="cert-radio-inline__item">
                    <input
                      type="radio"
                      name="edit-grade-mode"
                      value="details"
                      checked={editDraft.gradeMode === "details"}
                      onChange={handleEditFieldChange("gradeMode")}
                    />
                    <span className="cert-radio-inline__text">
                      {t("cert.fields.gradeMode.details", { defaultValue: "Details (UNC/AU/XF/VF/F/VG/G)" })}
                    </span>
                  </label>
                  <label className="cert-radio-inline__item">
                    <input
                      type="radio"
                      name="edit-grade-mode"
                      value="authentic"
                      checked={editDraft.gradeMode === "authentic"}
                      onChange={handleEditFieldChange("gradeMode")}
                    />
                    <span className="cert-radio-inline__text">
                      {t("cert.fields.gradeMode.authentic", { defaultValue: "Authentic only (no grade)" })}
                    </span>
                  </label>
                </div>
                {editDraft.gradeMode === "numeric" ? (
                  <label>
                    {t("nft.meta.grade")}
                    <input
                      type="text"
                      value={editDraft.grade}
                      onChange={handleEditFieldChange("grade")}
                      onBlur={markEditTouched("grade")}
                      className="cert-input"
                      required
                    />
                    {renderEditFieldHint(t("cert.fields.grade.hint", { defaultValue: "Grade shown on the slab (e.g., MS61, PF69)." }))}
                    {renderEditFieldError("grade")}
                  </label>
                ) : null}
                {editDraft.gradeMode === "details" ? (
                  <label>
                    {t("cert.fields.detailsTerm.label", { defaultValue: "Details grade" })}
                    <select
                      className="cert-input cert-input--select"
                      value={editDraft.detailsTerm}
                      onChange={handleEditFieldChange("detailsTerm")}
                      onBlur={markEditTouched("detailsTerm")}
                      required
                    >
                      <option value="">
                        {t("cert.fields.detailsTerm.placeholder", { defaultValue: "Choose details grade" })}
                      </option>
                      {sheldonDetailsTermOptions.map((term) => (
                        <option key={term.code} value={term.code}>
                          {term.display_name || term.code.replace("_DETAILS", "")}
                        </option>
                      ))}
                    </select>
                    {renderEditFieldHint(
                      t("cert.fields.detailsTerm.hint", { defaultValue: "Select the Details adjective that appears on the slab." })
                    )}
                    {renderEditFieldError("detailsTerm")}
                  </label>
                ) : null}
                {editDraft.gradeMode !== "authentic" && strikeOptions.length > 0 ? (
                  <label>
                    {t("cert.fields.strikeChar.label", { defaultValue: "Strike characters" })}
                    <select
                      className="cert-input cert-input--select"
                      value={editDraft.strikeChar}
                      onChange={handleEditFieldChange("strikeChar")}
                      onBlur={markEditTouched("strikeChar")}
                    >
                      <option value="">
                        {t("common.none", { defaultValue: "None" })}
                      </option>
                      {strikeOptions.map((code) => (
                        <option key={code} value={code.toLowerCase()}>
                          {code}
                        </option>
                      ))}
                    </select>
                    {renderEditFieldHint(
                      editDraft.gradeMode === "details"
                        ? t("cert.fields.strikeChar.hintDetails", { defaultValue: "Optional RD/RB/BN" })
                        : t("cert.fields.strikeChar.hint", { defaultValue: "Optional RD/RB/BN/FB/FS" })
                    )}
                    {renderEditFieldError("strikeChar")}
                  </label>
                ) : null}
                {editDraft.gradeMode === "numeric" ? (
                  <div className="cert-checkbox-group">
                    <label className="cert-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(editDraft.bonusPlus)}
                        onChange={handleEditFieldChange("bonusPlus")}
                      />
                      +
                    </label>
                    <label className="cert-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(editDraft.bonusStar)}
                        onChange={handleEditFieldChange("bonusStar")}
                      />
                      ★
                    </label>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <label>
                  {t("cert.fields.gradeTerm.label", { defaultValue: "Grade term" })}
                  <select
                    className="cert-input cert-input--select"
                    value={editDraft.termCode}
                    onChange={handleEditFieldChange("termCode")}
                    onBlur={markEditTouched("termCode")}
                    required
                  >
                    <option value="">
                      {t("cert.fields.gradeTerm.placeholder", { defaultValue: "Select…" })}
                    </option>
                    {effectiveAncientTermOptions.map((term) => (
                      <option key={term.code} value={term.code}>
                        {term.display_name || term.code}
                      </option>
                    ))}
                  </select>
                  {renderEditFieldHint(
                    t("cert.fields.gradeTerm.hint", { defaultValue: "Descriptive grade term on the slab (Ancients)." })
                  )}
                  {renderEditFieldError("termCode")}
                </label>
                <label>
                  {t("cert.fields.gradeStrike.label", { defaultValue: "Strike" })}
                  <select
                    className="cert-input cert-input--select"
                    value={editDraft.strike === null || editDraft.strike === undefined ? "" : String(editDraft.strike)}
                    onChange={handleEditFieldChange("strike")}
                    onBlur={markEditTouched("strike")}
                  >
                    <option value="">{t("cert.fields.optional", { defaultValue: "Optional" })}</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>{`${value}/5`}</option>
                    ))}
                  </select>
                  {renderEditFieldHint(t("cert.fields.gradeStrike.hint", { defaultValue: "Optional sub-score (1–5)." }))}
                  {renderEditFieldError("strike")}
                </label>
                <label>
                  {t("cert.fields.gradeSurface.label", { defaultValue: "Surface" })}
                  <select
                    className="cert-input cert-input--select"
                    value={editDraft.surface === null || editDraft.surface === undefined ? "" : String(editDraft.surface)}
                    onChange={handleEditFieldChange("surface")}
                    onBlur={markEditTouched("surface")}
                  >
                    <option value="">{t("cert.fields.optional", { defaultValue: "Optional" })}</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>{`${value}/5`}</option>
                    ))}
                  </select>
                  {renderEditFieldHint(t("cert.fields.gradeSurface.hint", { defaultValue: "Optional sub-score (1–5)." }))}
                  {renderEditFieldError("surface")}
                </label>
                <label>
                  {t("cert.fields.gradeStyle.label", { defaultValue: "Style" })}
                  <input
                    type="text"
                    value={editDraft.style}
                    onChange={handleEditFieldChange("style")}
                    onBlur={markEditTouched("style")}
                    className="cert-input"
                  />
                  {renderEditFieldHint(
                    `${t("cert.fields.gradeStyle.hint", { defaultValue: "Optional style text." }).replace(/\.$/, "")} (${t(
                      "cert.fields.gradeStyle.placeholder",
                      { defaultValue: "e.g., Fine Style" }
                    ).replace(/^ej\./, "Ej.")}).`
                  )}
                  {renderEditFieldError("style")}
                </label>
                <label className="nft-edit-form__checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(editDraft.detailsFlag)}
                    onChange={handleEditFieldChange("detailsFlag")}
                  />
                  <span>
                    {t("cert.fields.gradeDetailsFlag.inline", {
                      defaultValue: "Toggle when the item has a details issue",
                    })}
                  </span>
                </label>
                {editDraft.detailsFlag ? (
                  <label>
                    {t("cert.fields.gradeDetailsReason.label")}
                    <input
                      type="text"
                      value={editDraft.detailsReason}
                      onChange={handleEditFieldChange("detailsReason")}
                      onBlur={markEditTouched("detailsReason")}
                      className="cert-input"
                    />
                    {renderEditFieldHint(
                      t("cert.fields.gradeDetailsReason.hint", { defaultValue: "Optional (e.g., Cleaned, Scratched)." })
                    )}
                    {renderEditFieldError("detailsReason")}
                  </label>
                ) : null}
              </>
            )}
            <label>
              {t("nft.meta.collection")}
              <input
                type="text"
                value={editDraft.collection_name}
                onChange={handleEditFieldChange("collection_name")}
                onBlur={markEditTouched("collection_name")}
                className="cert-input"
              />
              {renderEditFieldError("collection_name")}
            </label>
            <label>
              {t("nft.meta.description")}
              <textarea
                rows={4}
                value={editDraft.description}
                onChange={handleEditFieldChange("description")}
                onBlur={markEditTouched("description")}
                className="cert-input cert-input--textarea"
              />
              {renderEditFieldError("description")}
            </label>
            {canPromoteToTrusted && (
              <label className="nft-edit-form__checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(editDraft.promoteTrusted)}
                  onChange={handleEditFieldChange("promoteTrusted")}
                />
                <span>{t("nft.edit.promoteToTrusted")}</span>
              </label>
            )}
            {editMessage && editMessage.type === "error" && (
              <p className="exposure-form__error">{editMessage.text}</p>
            )}
            <div className="exposure-modal__actions">
              <button
                type="button"
                className="exposure-modal__button exposure-modal__button--secondary"
                onClick={handleCloseEdit}
                disabled={isEditSubmitting}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="exposure-modal__button exposure-modal__button--primary"
                disabled={isEditSubmitting || !isEditValid}
              >
                {isEditSubmitting ? t("common.saving") : t("nft.edit.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleDownloadQr = () => {
    if (!qrPreview) return;
    const platform = activeExposure?.platform_name || t("nft.exposure.marketplaceFallback");
    downloadDataUrl(qrPreview, `${platform.toLowerCase()}-numistrail-qr.png`);
    setExposureMessage({ type: "info", text: t("nft.exposure.downloaded", { platform }) });
  };

  const renderExposureModal = () => {
    if (!exposureModal.open) return null;
    const closeWithoutCancel = () => handleCloseExposureModal({ skipCancel: true });
    let title = "";
    let body = null;
    let actions = null;
    if (exposureModal.step === "mode") {
      title = t("nft.exposure.modeTitle");
      body = (
        <div className="exposure-mode">
          <button type="button" className="exposure-mode__option" onClick={() => handleExposureModeSelect("present")}>
            <strong>{t("nft.exposure.mode.present.title")}</strong>
            <span>{t("nft.exposure.mode.present.desc")}</span>
          </button>
          <button type="button" className="exposure-mode__option" onClick={() => handleExposureModeSelect("not_present")}>
            <strong>{t("nft.exposure.mode.remote.title")}</strong>
            <span>{t("nft.exposure.mode.remote.desc")}</span>
          </button>
        </div>
      );
      actions = (
        <button
          type="button"
          className="exposure-modal__button exposure-modal__button--secondary"
          onClick={closeWithoutCancel}
        >
          {t("common.cancel")}
        </button>
      );
    } else if (exposureModal.step === "marketplace") {
      title = t("nft.exposure.marketplaceTitle");
      const startMin = toLocalInputValue(new Date(Date.now() - MAX_PAST_START_MS));
      const matchedMarketplace = matchMarketplace(marketplaceDraft.platform);
      const platformLabel = matchedMarketplace?.display_name || matchedMarketplace?.slug || t("nft.exposure.selectedPlatform");
      body = (
        <form className="exposure-form" onSubmit={handleCreateMarketplaceExposure}>
          <label>
            {t("nft.exposure.start")}
            <input
              type="datetime-local"
              value={marketplaceDraft.startAt}
              min={startMin}
              onChange={handleMarketplaceFieldChange("startAt")}
              required
            />
          </label>
          <label>
            {t("nft.exposure.end")}
            <input
              type="datetime-local"
              value={marketplaceDraft.endAt}
              min={marketplaceDraft.startAt || startMin}
              onChange={handleMarketplaceFieldChange("endAt")}
              required
            />
          </label>
          <label>
            {t("nft.exposure.marketplace")}
            <select
              value={marketplaceDraft.platform}
              onChange={handleMarketplaceFieldChange("platform")}
              required
              disabled={marketplacesLoading}
            >
              <option value="">{t("nft.exposure.marketplacePlaceholder")}</option>
              {marketplaces.map((item) => {
                const value = item.slug || item.display_name || item.company_id;
                const label = item.display_name || item.slug || t("nft.exposure.marketplaceFallback");
                return (
                  <option key={item.company_id || value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
            {marketplacesLoading && <p className="exposure-form__hint">{t("nft.marketplaces.loading")}</p>}
            {marketplaceLoadError && (
              <p className="exposure-form__error">
                {marketplaceLoadError}{" "}
                <button
                  type="button"
                  className="exposure-form__retry"
                  onClick={loadMarketplaces}
                >
                  {t("common.retry")}
                </button>
              </p>
            )}
            {!marketplacesLoading && !marketplaceLoadError && !marketplaces.length && (
              <p className="exposure-form__hint">{t("nft.marketplaces.none")}</p>
            )}
          </label>
          <label>
            {t("nft.exposure.usernameLabel", { platform: platformLabel })}
            <input
              type="text"
              value={marketplaceDraft.username}
              onChange={handleMarketplaceFieldChange("username")}
              placeholder={t("nft.exposure.usernamePlaceholder", { platform: platformLabel })}
              required
            />
            <p className="exposure-form__hint">{t("nft.exposure.usernameHint")}</p>
          </label>
          {exposureError && <p className="exposure-form__error">{exposureError}</p>}
          <div className="exposure-modal__actions">
            <button
              type="button"
              className="exposure-modal__button exposure-modal__button--secondary"
              onClick={closeWithoutCancel}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="exposure-modal__button exposure-modal__button--primary"
              disabled={exposureLoading}
            >
              {exposureLoading ? t("common.saving") : t("nft.exposure.share")}
            </button>
          </div>
        </form>
      );
    } else if (exposureModal.step === "present") {
      title = t("nft.exposure.presentTitle");
      const confirmationCode = activeExposure?.latest_event?.confirmation_code;
      body = (
        <>
          <p className="exposure-hint">{t("nft.exposure.presentHint")}</p>
          <div className="qr-preview">
            {qrPreview ? <img src={buildDataUrl(qrPreview)} alt={t("nft.exposure.presentAlt")} /> : <p>{t("nft.exposure.generating")}</p>}
          </div>
          <p className="exposure-validity">{t("nft.exposure.expiresAt", { time: formatDateTime(activeExposure?.expires_at) })}</p>
          {confirmationCode && (
            <div className="exposure-code-panel">
              <p>{t("nft.exposure.codeLabel")}</p>
              <strong>{confirmationCode}</strong>
              <p className="exposure-code-panel__hint">{t("nft.exposure.codeHint")}</p>
            </div>
          )}
          {exposureError && <p className="exposure-form__error">{exposureError}</p>}
        </>
      );
      actions = (
        <>
          {confirmationCode && (
            <button
              type="button"
              className="exposure-modal__button exposure-modal__button--primary"
              onClick={() => {
                handleCloseExposureModal({ skipCancel: true });
                handleTransfer();
              }}
              disabled={exposureLoading}
            >
              {t("nft.actions.transfer")}
            </button>
          )}
          <button
            type="button"
            className="exposure-modal__button exposure-modal__button--secondary"
            onClick={() => handleCloseExposureModal()}
            disabled={exposureLoading}
          >
            {hasActiveExposure && isFaceToFaceExposure ? t("common.cancel") : t("common.close")}
          </button>
        </>
      );
    } else if (exposureModal.step === "remote-qr") {
      title = t("nft.exposure.remoteTitle", { platform: exposureModal.context?.platform || t("nft.exposure.marketplaceFallback") });
      body = (
        <>
          <p className="exposure-hint">
            {t("nft.exposure.remoteHint", { platform: exposureModal.context?.platform || t("nft.exposure.marketplaceFallback") })}
          </p>
          <div className="qr-preview">
            {qrPreview ? <img src={buildDataUrl(qrPreview)} alt={t("nft.exposure.remoteAlt")} /> : <p>{t("nft.exposure.loading")}</p>}
          </div>
          {qrPayload && <p className="exposure-code-panel__hint exposure-code-panel__hint--muted">{t("nft.exposure.payloadLabel", { payload: qrPayload })}</p>}
          {exposureError && <p className="exposure-form__error">{exposureError}</p>}
        </>
      );
      actions = (
        <>
          <button
            type="button"
            className="exposure-modal__button exposure-modal__button--secondary"
            onClick={() => handleCloseExposureModal({ skipCancel: true })}
          >
            {t("common.close")}
          </button>
          <button
            type="button"
            className="exposure-modal__button exposure-modal__button--primary"
            onClick={handleDownloadQr}
            disabled={!qrPreview || exposureLoading}
          >
            {t("nft.exposure.download")}
          </button>
        </>
      );
    }

    return (
      <div className="exposure-modal" role="dialog" aria-modal="true">
        <div
          className="exposure-modal__backdrop"
          onClick={() => handleCloseExposureModal({ skipCancel: exposureModal.step !== "present" })}
        />
        <div className="exposure-modal__card">
          <header className="exposure-modal__header">
            <h2>{title}</h2>
            <button
              type="button"
              className="exposure-modal__close"
              onClick={() => handleCloseExposureModal({ skipCancel: exposureModal.step !== "present" })}
            >
              ×
            </button>
          </header>
          <div className="exposure-modal__body">{body}</div>
          {actions && exposureModal.step !== "marketplace" && (
            <div className="exposure-modal__actions">{actions}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="nft-detail-screen">
      <div className="nft-detail-shell">
        {canShowBack && (
          <button type="button" className="nft-detail-back" onClick={handleBack}>
            ← {t("common.back")}
          </button>
        )}

        {isLoading && (
          <div className="nft-detail-loading" role="status">
            {t("nft.loading")}
          </div>
        )}

        {error && !isLoading && (
          <div className="nft-detail-error" role="alert">
            <p className="nft-detail-error__text">{error}</p>
            <button
              type="button"
              className="nft-detail-error__button"
              onClick={isPrivateAccessError ? handleCloseError : handleRetry}
            >
              {isPrivateAccessError ? t("common.close") : t("common.retry")}
            </button>
          </div>
        )}

        {!isLoading && !error && detail && (
          <div className="nft-detail-content">
            <div className="nft-detail-card">
              <div className="nft-detail-hero">
                <div className="nft-detail-thumb" aria-hidden="true">
                  <img
                    src={heroThumbSrc}
                    alt={`${title} thumbnail`}
                    className="nft-detail-thumb__img"
                  />
                </div>
                <div className="nft-detail-hero__body">
                  <h1 className="nft-detail-title">{title}</h1>
                  <div className="nft-detail-badges">
                    {year !== null && year !== undefined && String(year).trim() ? (
                      <LabelBadge>{t("nft.badge.year", { value: year })}</LabelBadge>
                    ) : null}
                    {mintBadgeValue ? (
                      <LabelBadge>{t("nft.badge.mint", { value: mintBadgeValue })}</LabelBadge>
                    ) : null}
                    {metal && <Badge>{metal}</Badge>}
                    <Badge>{detail.certification_authority}</Badge>
                    <Badge>{t("nft.certNumber", { number: detail.certification_number })}</Badge>
                    <Badge tone={isTrustedOrigin ? "positive" : "neutral"} title={originTooltip}>{originLabel}</Badge>
                    {gradeDisplay && <LabelBadge>{t("nft.badge.grade", { value: gradeDisplay })}</LabelBadge>}
                    {gradeInfo?.system && gradeInfo.system !== "sheldon"
                      ? gradeAttributePieces.map((piece) => (
                          <LabelBadge key={piece}>{piece}</LabelBadge>
                        ))
                      : null}
                    {collectionName ? (
                      <LabelBadge title={collectionName}>
                        {t("nft.collection", { name: collectionName })}
                      </LabelBadge>
                    ) : null}
                  </div>
                  <div className="nft-detail-state">
                    <Badge tone={verificationTone}>{t("nft.labels.verification", { status: verificationLabel })}</Badge>
                    <Badge tone={isPublic ? "positive" : "neutral"}>{t("nft.labels.visibility", { status: visibilityBadgeLabel })}</Badge>
                  </div>
                </div>
              </div>

              {shouldShowActions && (
                  <div className="nft-detail-actions">
                    {canEditMetadata && (
                      <ActionButton type="button" onClick={handleOpenEdit} tooltip={t("nft.actionsHelp.edit")}>
                        {t("nft.actions.edit")}
                      </ActionButton>
                    )}
                    {canManageActions && (
                      <>
                        {!hasActiveExposure && (
                          <ActionButton
                            type="button"
                            onClick={handleExposeClick}
                            disabled={isShareForVerificationDisabled}
                            tooltip={shareForVerificationTooltip}
                          >
                            {t("nft.actions.shareForVerification")}
                          </ActionButton>
                        )}
                      {isFaceToFaceExposure && hasActiveExposure && (
                        <ActionButton
                          type="button"
                          onClick={() => {
                            resetExposureModal();
                            setExposureModal({ open: true, step: "present", context: null });
                          }}
                          tooltip={t("nft.actionsHelp.viewQr")}
                        >
                          {t("nft.actions.viewQr")}
                        </ActionButton>
                      )}
                      {isRemoteExposure && (
                        <>
                          <ActionButton type="button" onClick={handleShowQr} tooltip={t("nft.actionsHelp.getQr")}>
                            {t("nft.actions.getQr")}
                          </ActionButton>
                          <ActionButton type="button" onClick={handleStopExposure} tooltip={t("nft.actionsHelp.stopExposure")}>
                            {t("nft.actions.stopExposure")}
                          </ActionButton>
                        </>
                      )}
                      <ActionButton type="button" onClick={handleShareClick} tooltip={shareLinkTooltip}>
                        {t("nft.actions.shareLink")}
                      </ActionButton>
                      {canToggleVisibility && (
                        <ActionButton
                          type="button"
                          onClick={handleToggleVisibility}
                          disabled={isVisibilityLoading}
                          tooltip={!isVisibilityLoading ? visibilityTooltip : null}
                        >
                          {isVisibilityLoading ? t("common.saving") : visibilityButtonLabel}
                        </ActionButton>
                      )}
                      <ActionButton
                        type="button"
                        onClick={handleTransfer}
                        disabled={isTransferDisabled}
                        tooltip={transferTooltip}
                      >
                        {t("nft.actions.transfer")}
                      </ActionButton>
                      {consignedUserId && consignedUserId === userId && (
                        <ActionButton
                          type="button"
                          onClick={handleConsignmentReturn}
                          disabled={isConsignmentAction}
                          tooltip={!isConsignmentAction ? t("nft.actionsHelp.returnToOwner") : null}
                        >
                          {isConsignmentAction ? t("nft.actions.returning") : t("nft.actions.returnToOwner")}
                        </ActionButton>
                      )}
                    </>
                  )}
                </div>
              )}
              {visibilityMessage && (
                <p className={`nft-detail-status nft-detail-status--${visibilityMessage.type}`}>
                  {visibilityMessage.text}
                </p>
              )}
              {editMessage && editMessage.type !== "error" && (
                <p className={`nft-detail-status nft-detail-status--${editMessage.type}`}>
                  {editMessage.text}
                </p>
              )}
              {activeExposure && (
                <div className="nft-exposure-card">
                  <div className="nft-exposure-card__header">
                    <div>
                      <p className="nft-exposure-card__label">{t("nft.exposure.current")}</p>
                      <p className="nft-exposure-card__title">
                        {activeExposure.exposure_type === "present" ? t("nft.exposure.mode.present.title") : `${activeExposure.platform_name || t("nft.exposure.marketplaceFallback")} ${t("nft.exposure.listing")}`}
                      </p>
                    </div>
                    <Badge tone={hasActiveExposure ? "positive" : "neutral"}>{(exposureStatus || "inactive").toUpperCase()}</Badge>
                  </div>
                  <ul className="nft-exposure-card__meta">
                    <li>
                      <span>{t("nft.exposure.validFrom")}</span>
                      <strong>{formatDateTime(activeExposure.start_at)}</strong>
                    </li>
                    {activeExposure.expires_at && (
                      <li>
                        <span>{t("nft.exposure.validUntil")}</span>
                        <strong>{formatDateTime(activeExposure.expires_at)}</strong>
                      </li>
                    )}
                    {isRemoteExposure && (
                      <>
                        <li>
                          <span>{t("nft.exposure.marketplace")}</span>
                          <strong>{activeExposure.platform_name}</strong>
                        </li>
                        <li>
                          <span>{t("nft.exposure.usernameLabel", { platform: activeExposure.platform_name || t("nft.exposure.marketplaceFallback") })}</span>
                          <strong>{activeExposure.platform_username}</strong>
                        </li>
                      </>
                    )}
                  </ul>
                  {isRemoteExposure && (
                    <p className="nft-exposure-card__notice">⚠ {t("nft.exposure.noticeRemote")}</p>
                  )}
                  {isFaceToFaceExposure && (
                    <p className="nft-exposure-card__notice">{t("nft.exposure.noticePresent")}</p>
                  )}
                </div>
              )}
              {exposureMessage && (
                <p className={`nft-detail-status nft-detail-status--${exposureMessage.type}`}>
                  {exposureMessage.text}
                </p>
              )}
              {shareMessage && (
                <p className={`nft-detail-status nft-detail-status--${shareMessage.type}`}>
                  {shareMessage.text}
                </p>
              )}
              {consignedUserId && (
                <div className="nft-detail-consignment">
                  <p>
                    {t("nft.consignedTo", {
                      name: consignedUserId === userId
                        ? t("nft.you")
                        : (consignedUserName ? maskIdForDisplay(consignedUserName) : consignedUserId),
                    })}
                  </p>
                </div>
              )}
              {consignmentMessage && (
                <p className={`nft-detail-status nft-detail-status--${consignmentMessage.type}`}>
                  {consignmentMessage.text}
                </p>
              )}
            </div>

            <Section title={t("nft.metadataSection")}>
              {metadataEntries.length === 0 ? (
                <p className="nft-detail-empty">{t("nft.metadataEmpty")}</p>
              ) : (
                <dl className="nft-detail-metadata">
                  {metadataEntries.map(([label, value]) => (
                    <React.Fragment key={label}>
                      <dt>{label}</dt>
                      <dd>{renderMetadataValue(value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </Section>

            <Section
              title={t("nft.trailSection")}
              right={timelineHeaderControl}
            >
              {timelineEntries.length === 0 ? (
                <p className="nft-detail-empty">{t("nft.trailEmpty")}</p>
              ) : (
                <>
                  {timelinePreviewLabel && (
                    <p className="nft-detail-empty">{timelinePreviewLabel}</p>
                  )}
                  <div className="nft-detail-timeline">
                    {visibleTimelineEntries.map((entry) => (
                      <TimelineItem key={entry.id} entry={applyFriendlyNamesToEntry(entry, maskIdForDisplay)} />
                    ))}
                  </div>
                </>
              )}
            </Section>

            <Section
              title={t("nft.notesSection")}
              right={notesHeaderControl}
            >
              {noteMessage && (
                <p className={`nft-detail-status nft-detail-status--${noteMessage.type}`}>
                  {noteMessage.text}
                </p>
              )}
              {isNoteComposerOpen && (
                <form className="nft-note-composer" onSubmit={handleCreateNote}>
                  <div className="nft-note-toolbar">
                    <button
                      type="button"
                      className="nft-note-toolbar__button"
                      onClick={applyNoteFormatting("**")}
                      aria-label={t("nft.notes.format.bold")}
                      title={t("nft.notes.format.bold")}
                    >
                      B
                    </button>
                    <button
                      type="button"
                      className="nft-note-toolbar__button"
                      onClick={applyNoteFormatting("*")}
                      aria-label={t("nft.notes.format.italic")}
                      title={t("nft.notes.format.italic")}
                    >
                      I
                    </button>
                    <button
                      type="button"
                      className="nft-note-toolbar__button"
                      onClick={applyNoteFormatting("__")}
                      aria-label={t("nft.notes.format.underline")}
                      title={t("nft.notes.format.underline")}
                    >
                      U
                    </button>
                  </div>
                  <label className="nft-note-label" htmlFor="nft-note-body">
                    <span>{t("nft.notes.inputLabel")}</span>
                    <textarea
                      id="nft-note-body"
                      ref={noteInputRef}
                      className="nft-note-textarea"
                      value={noteDraft}
                      onChange={handleNoteChange}
                      maxLength={noteMaxLength}
                      placeholder={t("nft.notes.placeholder")}
                      rows={5}
                    />
                  </label>
                  <div className="nft-note-footer">
                    <span className="nft-note-hint">
                      {t("nft.notes.limit", { max: noteMaxLength })}
                    </span>
                    <span className={`nft-note-count${isNoteTooLong ? " nft-note-count--error" : ""}`}>
                      {noteCountLabel}
                    </span>
                  </div>
                  <div className="nft-note-actions">
                    <button type="button" className="nft-note-action" onClick={handleNoteCancel} disabled={isNoteSubmitting}>
                      {t("nft.notes.cancel")}
                    </button>
                    <button
                      type="submit"
                      className="nft-note-action nft-note-action--primary"
                      disabled={isNoteSubmitting || !noteDraftTrimmed || isNoteTooLong}
                    >
                      {isNoteSubmitting ? t("nft.notes.saving") : t("nft.notes.save")}
                    </button>
                  </div>
                  {noteDraftTrimmed && (
                    <div className="nft-note-preview">
                      <p className="nft-note-preview__label">{t("nft.notes.previewLabel")}</p>
                      <div className="nft-note-preview__body">
                        {renderMarkdown(noteDraft)}
                      </div>
                    </div>
                  )}
                </form>
              )}
              {noteEntries.length === 0 ? (
                <p className="nft-detail-empty">{t("nft.notesEmpty")}</p>
              ) : (
                <>
                  {notesPreviewLabel && (
                    <p className="nft-detail-empty">{notesPreviewLabel}</p>
                  )}
                  <div className="nft-notes">
                    {visibleNotes.map((note) => {
                      const authorLabel = note.created_by === userId
                        ? t("nft.you")
                        : (note.created_by_numistrail_id ? maskIdForDisplay(note.created_by_numistrail_id) : t("nft.notes.authorFallback"));
                      const body = note.format === "markdown"
                        ? renderMarkdown(note.body)
                        : note.body;
                      return (
                        <article key={note.id} className="nft-note">
                          <div className="nft-note__meta">
                            <span className="nft-note__author">
                              {t("nft.notes.addedBy", { name: authorLabel })}
                            </span>
                            <span className="nft-note__date">
                              {formatDateTime(note.created_at)}
                            </span>
                          </div>
                          <div className="nft-note__body">{body}</div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </Section>
          </div>
        )}
      </div>
      {renderConfirmModal({
        open: shareModalOpen,
        title: t("nft.visibility.sharePrompt.title"),
        body: t("nft.visibility.sharePrompt.body"),
        confirmLabel: t("nft.visibility.sharePrompt.confirm"),
        cancelLabel: t("nft.visibility.sharePrompt.cancel"),
        onConfirm: confirmShareMakePublic,
        onCancel: () => setShareModalOpen(false),
      })}
      {renderConfirmModal({
        open: privateModalOpen,
        title: t("nft.visibility.privatePrompt.title"),
        body: t("nft.visibility.privatePrompt.body"),
        confirmLabel: t("nft.visibility.privatePrompt.confirm"),
        cancelLabel: t("nft.visibility.privatePrompt.cancel"),
        onConfirm: confirmMakePrivate,
        onCancel: () => setPrivateModalOpen(false),
      })}
      {renderEditModal()}
      {renderExposureModal()}
    </div>
  );
}

export default NftDetails;


